(function() {
  'use strict';

  // Configuration
  const STORES_PER_PAGE = 6;
  const DEBOUNCE_DELAY = 300;
  const FEATURABLE_SCRIPT_SRC = 'https://featurable.com/assets/bundle.js';
  const EARTH_RADIUS_KM = 6371;
  // Radius used to restrict area-search suggestions to "the user's city" once a
  // location is known - a circle around the city centre approximates city limits
  // well enough since we don't have real administrative boundary polygons.
  const CITY_SUGGESTION_RADIUS_METERS = 30000;

  // Google Maps JS API key - restricted by HTTP referrer to this store's domains.
  // The raw Geocoding REST API rejects referrer-restricted keys (Google only allows
  // those with the client-side Maps JavaScript API), so geocoding goes through
  // google.maps.importLibrary('geocoding') + Geocoder below, not a REST fetch.
  const GOOGLE_MAPS_API_KEY = 'AIzaSyCFY97eRZdzOmNysL42Yu4RIOacZ6-Ot20';

  // Guards against a race between the independent, concurrent city/pincode
  // resolution flows (page-load initializeFromPincode, the "Use My Location"
  // click, the header's pincodeUpdated event, and area search): each bumps
  // this before starting async work and checks it's still current before
  // touching the UI, so a slower, stale resolution can never overwrite a
  // newer one's result.
  let locationRequestId = 0;

  // Element references
  let cityInput, areaInput, searchButton, storeContainer, noResultsMessage, paginationContainer, errorMessage, infoMessage, loadingState, loadingText, locationBanner, locationBannerText, locationBannerBtn, areaSuggestionsMenu, resetButton, areaSearchWrapper, searchRow;
  let pincodePanel, pincodeInput, pincodeSubmitBtn, pincodeInputError;
  let allStoresData = [];
  let filteredStores = [];
  let currentPage = 1;
  // True only once performAreaSearch() has actually run (Search click/Enter) for
  // the area text currently in the box - lets the empty state distinguish "you're
  // still typing, nothing literally matches yet" from "we actually searched
  // (including the geocode fallback) and found nothing."
  let areaSearchSubmitted = false;
  // The last city the user explicitly chose (dropdown pick, or resolved from
  // pincode/geolocation). Kept separately from cityInput.value because typing
  // in the area box blanks cityInput.value for UI mutual-exclusivity, but the
  // user's city context should still restrict area-search suggestions.
  let lastSelectedCity = null;
  let cityMapping = {};
  let debounceTimer = null;
  let errorMessageTimer = null;
  let shouldScrollToError = false;
  let featurableScriptRequested = false;
  let featurableScriptLoaded = false;
  let featurableObserver = null;
  // renderStores() can legitimately fire several times in quick succession
  // during a single page load (unsorted render, then re-sorted once location
  // resolves, etc.) - these two flags coalesce those bursts into a single
  // in-flight retrigger cycle (plus one trailing follow-up if new empty
  // widgets appeared while it was running) instead of each call independently
  // scheduling its own init/re-fetch, which was multiplying widget requests.
  let featurableRetriggerInFlight = false;
  let featurableRetriggerPending = false;
  let featurableReinjectCount = 0;
  const FEATURABLE_MAX_REINJECTS = 2;
  let storeCardsMap = new Map();
  const pincodeGeocodeCache = new Map(); // pincode -> { city, lat, lng }, avoids repeat billed lookups
  const areaGeocodeCache = new Map(); // lowercased area text -> {lat, lng, formattedAddress} | null

  // Featurable Configuration & Styles
  const FEATURABLE_CONFIG = {
    styleId: 'featurable-store-locator-overrides',
    selectors: {
      container: '#featurable-container',
      body: '.widget__body',
      bgWhite: '[class*="Badge-module__bgWhite"]',
      branding: '[class*="Badge-module__branding"]',
      googleIcon: '.badge__google-icon',
      badgeContainer: '.badge__container',
      badgeInfo: '.badge__info',
      ratingContainer: '.badge__rating-container',
      starsContainer: '.badge__stars-container',
      starRating: '.star-rating',
      starFill: '.star-rating__fill',
      starEmpty: '.star-rating__empty',
      starItem: '[class*="BadgeStarRating-module__star__container"]',
      starSvg: '[class*="BadgeStarRating-module__star--"]',
      loading: '[class*="App-module__loadingContainer"]',
      errorClass: '[class*="App-module__errorClass"]'
    },
    getStyles: function() {
      const s = this.selectors;
      return `
        ${s.container} { padding: 0 !important; }
        ${s.body} { padding: 0 !important; min-width: min-content !important; width: 175px !important; }
        ${s.bgWhite} { background: transparent !important; }
        ${s.branding}, ${s.googleIcon}, ${s.loading} { display: none !important; }
        ${s.badgeContainer} { border: 0 !important; box-shadow: none !important; text-align: left !important; justify-content: flex-start !important; display: flex !important; align-items: center !important; padding: 0 !important; }
        ${s.badgeContainer} > div:first-child { display: none !important; }
        ${s.badgeInfo} { padding-left: 0 !important; display: flex !important; align-items: center !important; }
        ${s.badgeInfo} > :not(${s.ratingContainer}) { display: none !important; }
        .badge__link-container { display: none !important; }
        ${s.ratingContainer} { justify-content: flex-start !important; display: flex !important; align-items: center !important; flex-direction: row-reverse !important; gap: 8px !important; }
        ${s.starsContainer}, ${s.starRating}, ${s.starFill}, ${s.starEmpty}, ${s.starItem} { display: flex !important; align-items: center !important; gap: 4px !important; }
        ${s.starSvg} { width: 24px !important; height: 24px !important; }
        ${s.errorClass} { display: none !important; padding: 0 !important; }
      `;
    }
  };

  function ensureFeaturableScriptLoaded() {
    if (featurableScriptRequested) return;
    const existingScript = document.querySelector(`script[src^="${FEATURABLE_SCRIPT_SRC}"]`);
    if (existingScript) {
      featurableScriptRequested = true;
      return;
    }

    const script = document.createElement('script');
    script.src = FEATURABLE_SCRIPT_SRC;
    script.defer = true;
    script.charset = 'UTF-8';
    script.setAttribute('data-featurable-bundle', 'true');
    script.onload = () => { featurableScriptLoaded = true; };
    document.head.appendChild(script);
    featurableScriptRequested = true;
  }

  function injectFeaturableShadowStyles(shadowRoot) {
    if (!shadowRoot || shadowRoot.querySelector(`style[data-featurable-custom="${FEATURABLE_CONFIG.styleId}"]`)) {
      return;
    }

    const style = document.createElement('style');
    style.setAttribute('data-featurable-custom', FEATURABLE_CONFIG.styleId);
    style.textContent = FEATURABLE_CONFIG.getStyles();
    shadowRoot.appendChild(style);
  }

  function fixFeaturableErrorMessages() {
    const widgets = document.querySelectorAll('[id^="featurable-"]');
    if (!widgets.length) return;

    widgets.forEach(widget => {
      // Check in shadow DOM
      let shadowHost = null;
      if (widget.shadowRoot) {
        shadowHost = widget;
      } else {
        const allDescendants = widget.querySelectorAll('*');
        for (const el of allDescendants) {
          if (el.shadowRoot) {
            shadowHost = el;
            break;
          }
        }
      }
      
      // Hide error messages in shadow DOM
      if (shadowHost && shadowHost.shadowRoot) {
        const errorElements = shadowHost.shadowRoot.querySelectorAll('[class*="App-module__errorClass"]');
        errorElements.forEach(errorEl => {
          const errorText = errorEl.textContent || errorEl.innerText;
          if (errorText && errorText.includes('Failed to load reviews')) {
            errorEl.style.display = 'none';
          }
        });
      }
      
      // Also check in regular DOM (in case error is rendered outside shadow DOM)
      const errorElements = widget.querySelectorAll('[class*="App-module__errorClass"]');
      errorElements.forEach(errorEl => {
        const errorText = errorEl.textContent || errorEl.innerText;
        if (errorText && errorText.includes('Failed to load reviews')) {
          errorEl.style.display = 'none';
        }
      });
    });
  }

  function applyFeaturableShadowStyles() {
    const widgets = document.querySelectorAll('[id^="featurable-"]');
    if (!widgets.length) return;

    widgets.forEach(widget => {
      // Find the shadow host - it's either the widget itself or one of its descendants
      let shadowHost = null;
      if (widget.shadowRoot) {
        shadowHost = widget;
      } else {
        // Deep search for any element with a shadow root inside the widget
        const allDescendants = widget.querySelectorAll('*');
        for (const el of allDescendants) {
          if (el.shadowRoot) {
            shadowHost = el;
            break;
          }
        }
      }
      
      if (shadowHost && shadowHost.shadowRoot) {
        injectFeaturableShadowStyles(shadowHost.shadowRoot);
      }
    });
    
    // Fix error messages after applying styles
    fixFeaturableErrorMessages();
  }

  function observeFeaturableWidgets() {
    if (featurableObserver) return;

    featurableObserver = new MutationObserver(() => {
      applyFeaturableShadowStyles();
      fixFeaturableErrorMessages();
    });

    featurableObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true
    });

    applyFeaturableShadowStyles();
    fixFeaturableErrorMessages();
  }

  function retriggerFeaturable() {
    const existingScript = document.querySelector(`script[src^="${FEATURABLE_SCRIPT_SRC}"]`);
    if (!existingScript) return;

    // We only need to re-trigger if there are widgets on the page that haven't loaded yet
    const unloadedWidgets = storeContainer.querySelectorAll('[id^="featurable-"]:empty');
    if (unloadedWidgets.length === 0) return;

    if (featurableRetriggerInFlight) {
      // A cycle is already running - let it finish, then re-check once more
      // rather than starting a second overlapping cycle of our own.
      featurableRetriggerPending = true;
      return;
    }
    featurableRetriggerInFlight = true;

    const tryInit = () => {
      featurableRetriggerInFlight = false;
      if (featurableRetriggerPending) {
        featurableRetriggerPending = false;
        retriggerFeaturable();
        return;
      }

      // Re-check at fire time - state may have changed since this was scheduled
      if (storeContainer.querySelectorAll('[id^="featurable-"]:empty').length === 0) return;

      if (window.featurable && typeof window.featurable.init === 'function') {
        window.featurable.init();
        return;
      }
      if (window.Featurable && typeof window.Featurable.init === 'function') {
        window.Featurable.init();
        return;
      }

      // The bundle genuinely finished loading but exposes no init hook we
      // recognise - fall back to a fresh, cache-busted script tag to force a
      // rescan of the DOM for new widgets. Capped so a persistently-missing
      // init hook (e.g. a referrer-blocked or genuinely init-less bundle)
      // can't loop into an unbounded number of duplicate script/API loads.
      if (featurableReinjectCount >= FEATURABLE_MAX_REINJECTS) return;
      featurableReinjectCount += 1;

      const newScript = document.createElement('script');
      newScript.src = `${FEATURABLE_SCRIPT_SRC}?v=${Date.now()}`;
      newScript.defer = true;
      newScript.charset = 'UTF-8';
      newScript.setAttribute('data-featurable-bundle', 'true');
      newScript.onload = () => { featurableScriptLoaded = true; };

      featurableScriptLoaded = false;
      existingScript.remove();
      document.head.appendChild(newScript);
      featurableScriptRequested = true;
    };

    if (featurableScriptLoaded) {
      // Already loaded earlier (e.g. widgets added on a later render) - a
      // short delay is enough for it to notice the newly-painted cards.
      setTimeout(tryInit, 200);
    } else {
      // Still in flight - wait for it to actually finish rather than guessing
      // with a fixed timeout. A blind 200ms fallback here used to fire a
      // wasteful duplicate script (and duplicate widget-data fetch) on nearly
      // every page load, since 200ms is rarely enough time for the real
      // external featurable.com fetch to complete.
      existingScript.addEventListener('load', () => setTimeout(tryInit, 100), { once: true });
      // Safety net in case the load event never fires for some reason.
      setTimeout(tryInit, 4000);
    }
  }

  // URL parameter utilities
  function getURLParameter(name) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name);
  }

  function updateURLParameter(name, value) {
    const url = new URL(window.location);
    if (value && value !== '1') {
      url.searchParams.set(name, value);
    } else {
      url.searchParams.delete(name);
    }
    window.history.replaceState({ page: value }, '', url);
  }

  // Initialize
  function init() {
    // Get elements
    cityInput = document.getElementById('store-search-location');
    areaInput = document.getElementById('store-search-input');
    searchButton = document.getElementById('search-button');
    storeContainer = document.getElementById('store-cards-container');
    noResultsMessage = document.getElementById('no-results-message');
    paginationContainer = document.getElementById('store-pagination');
    errorMessage = document.getElementById('store-locator-error-message');
    infoMessage = document.getElementById('store-locator-info-message');
    loadingState = document.getElementById('store-locator-loading-state');
    loadingText = document.getElementById('store-locator-loading-text');
    locationBanner = document.getElementById('store-locator-location-banner');
    locationBannerText = document.getElementById('store-locator-location-banner-text');
    locationBannerBtn = document.getElementById('store-locator-location-banner-btn');
    areaSuggestionsMenu = document.getElementById('store-locator-area-suggestions');
    resetButton = document.getElementById('reset-search-button');
    areaSearchWrapper = document.getElementById('store-locator-area-search-wrapper');
    searchRow = document.querySelector('.store-locator__search-row');
    pincodePanel = document.getElementById('store-locator-pincode-panel');
    pincodeInput = document.getElementById('store-locator-pincode-input');
    pincodeSubmitBtn = document.getElementById('store-locator-pincode-submit-btn');
    pincodeInputError = document.getElementById('store-locator-pincode-input-error');

    // Get store data from JSON
    const storeDataScript = document.getElementById('store-locator-data');
    if (storeDataScript) {
      try {
        allStoresData = JSON.parse(storeDataScript.textContent);
      } catch (e) {
        console.error('Error parsing store data:', e);
        return;
      }
    }

    // Get city mapping from data attribute
    const cityMappingScript = document.getElementById('store-city-mapping');
    if (cityMappingScript) {
      try {
        cityMapping = JSON.parse(cityMappingScript.textContent);
      } catch (e) {
        console.error('Error parsing city mapping:', e);
      }
    }

    // Reflect any already-known location (from earlier this session) immediately,
    // before initializeFromPincode has a chance to run/resolve anything new.
    updateLocationBanner();

    // Initialize from pincode
    initializeFromPincode().then(() => {
      // Read page from URL if present
      const urlPage = getURLParameter('page');
      let savedPage = null;
      if (urlPage) {
        const pageNum = parseInt(urlPage, 10);
        if (!isNaN(pageNum) && pageNum > 0) {
          savedPage = pageNum;
        }
      }
      
      // Set up event listeners
      setupEventListeners();
      
      // Filter stores (preserve page if loading from URL)
      filterStores(savedPage !== null);
      
      // Validate and set page from URL after filtering
      if (savedPage !== null) {
        const totalPages = Math.ceil(filteredStores.length / STORES_PER_PAGE);
        if (savedPage <= totalPages && savedPage > 0) {
          currentPage = savedPage;
        } else {
          // Invalid page, reset to 1 and update URL
          currentPage = 1;
          updateURLParameter('page', '1');
        }
      }
      
      // Initial render (will replace skeleton loaders)
      renderStores();
    });
  }

  // Get mapped city name (API city -> Store city)
  function getMappedCity(apiCity) {
    if (!apiCity) return '';
    const normalizedApiCity = apiCity.trim();
    if (cityMapping[normalizedApiCity]) {
      return cityMapping[normalizedApiCity];
    }
    return normalizedApiCity;
  }

  // Find matching city in dropdown options (case-insensitive, with partial matching fallback)
  function findMatchingCity(apiCity) {
    if (!apiCity) return '';
    const normalizedApiCity = apiCity.trim().toLowerCase();
    const dropdown = document.getElementById('store-search-location');
    if (!dropdown) return '';
    
    // First try exact match
    for (let i = 0; i < dropdown.options.length; i++) {
      const optionValue = dropdown.options[i].value.toLowerCase();
      if (optionValue === normalizedApiCity) {
        return dropdown.options[i].value;
      }
    }
    
    // If no exact match, try partial match (contains)
    for (let i = 0; i < dropdown.options.length; i++) {
      const optionValue = dropdown.options[i].value.toLowerCase();
      if (optionValue.includes(normalizedApiCity) || normalizedApiCity.includes(optionValue)) {
        return dropdown.options[i].value;
      }
    }
    
    return '';
  }

  // Pull the first matching address component (e.g. city, postal code) out of a
  // Google Geocoding API result set. Scans every result, since the most specific
  // (first) result doesn't always carry every component type.
  function extractAddressComponent(results, types) {
    if (!Array.isArray(results)) return null;
    for (const result of results) {
      if (!result.address_components) continue;
      for (const type of types) {
        const match = result.address_components.find((component) => component.types.includes(type));
        if (match) return match.long_name;
      }
    }
    return null;
  }

  // Official Google bootstrap loader (injects the Maps JS API script tag on first
  // use) so we can call google.maps.importLibrary(...) without a page-level <script>.
  function loadGoogleMapsBootstrap() {
    ((g) => {
      var h, a, k, p = 'The Google Maps JavaScript API', c = 'google', l = 'importLibrary', q = '__ib__', m = document, b = window;
      b = b[c] || (b[c] = {});
      var d = b.maps || (b.maps = {}), r = new Set(), e = new URLSearchParams(),
        u = () => h || (h = new Promise(async (f, n) => {
          await (a = m.createElement('script'));
          e.set('libraries', [...r] + '');
          for (k in g) e.set(k.replace(/[A-Z]/g, (t) => '_' + t[0].toLowerCase()), g[k]);
          e.set('callback', c + '.maps.' + q);
          a.src = `https://maps.${c}apis.com/maps/api/js?` + e;
          d[q] = f;
          a.onerror = () => (h = n(Error(p + ' could not load.')));
          a.nonce = m.querySelector('script[nonce]')?.nonce || '';
          m.head.append(a);
        }));
      d[l] ? console.warn(p + ' only loads once. Ignoring:', g) : (d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n)));
    })({ key: GOOGLE_MAPS_API_KEY, v: 'weekly' });
  }

  // Races a promise against a timeout so a hung/misconfigured Google Maps API
  // call (e.g. a referrer-restricted key rejecting silently) can never block
  // the store list from rendering.
  const GEOCODE_TIMEOUT_MS = 6000;
  function withTimeout(promise, ms) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error(`Timed out after ${ms}ms`)), ms);
      promise.then(
        (value) => { clearTimeout(timer); resolve(value); },
        (error) => { clearTimeout(timer); reject(error); }
      );
    });
  }

  let geocoderPromise = null;
  function getGeocoder() {
    if (!geocoderPromise) {
      loadGoogleMapsBootstrap();
      geocoderPromise = withTimeout(google.maps.importLibrary('geocoding'), GEOCODE_TIMEOUT_MS)
        .then(({ Geocoder }) => new Geocoder())
        .catch((error) => {
          geocoderPromise = null; // allow a retry on the next call instead of caching the failure forever
          throw error;
        });
    }
    return geocoderPromise;
  }

  // Lazily loads the Places library (separate from 'geocoding' - Google's
  // importLibrary supports loading additional libraries on demand after the
  // initial bootstrap script has already loaded).
  let placesLibraryPromise = null;
  function getPlacesLibrary() {
    if (!placesLibraryPromise) {
      loadGoogleMapsBootstrap();
      placesLibraryPromise = withTimeout(google.maps.importLibrary('places'), GEOCODE_TIMEOUT_MS)
        .catch((error) => {
          placesLibraryPromise = null;
          throw error;
        });
    }
    return placesLibraryPromise;
  }

  // A session token groups the autocomplete keystrokes for one search together
  // with the eventual place details fetch, so Google bills it as a single
  // session instead of per-request. Reset once a suggestion is picked (or the
  // search box is cleared) so the next search starts a fresh session.
  let autocompleteSessionToken = null;

  // Centre point to restrict area-search suggestions to, when the user's location
  // is known - either their actual detected/geocoded coordinates, or (when a city
  // was picked manually from the dropdown without coordinates, e.g. no geolocation
  // used) the average position of that city's own stores as a stand-in city centre.
  // Returns null when no location is known, meaning suggestions should stay global.
  function getCityRestrictionCenter() {
    const coords = getUserCoords();
    if (coords) return coords;

    const cityName = lastSelectedCity;
    if (!cityName) return null;

    const cityCoords = allStoresData
      .filter((store) => (store.city || '').trim().toLowerCase() === cityName.toLowerCase())
      .map((store) => ({ lat: parseFloat(store.latitude), lng: parseFloat(store.longitude) }))
      .filter((c) => !isNaN(c.lat) && !isNaN(c.lng));
    if (cityCoords.length === 0) return null;

    const avgLat = cityCoords.reduce((sum, c) => sum + c.lat, 0) / cityCoords.length;
    const avgLng = cityCoords.reduce((sum, c) => sum + c.lng, 0) / cityCoords.length;
    return { lat: avgLat, lng: avgLng };
  }

  // Square bounding box approximating a radius around a centre point, as a
  // LatLngBoundsLiteral. Places' AutocompleteSuggestion API silently over-restricts
  // (returns zero results) when given a plain {center, radius} circle literal for
  // locationRestriction, despite it being a documented type - a bounds literal is
  // the shape Google's own working examples actually use, so we compute one here.
  function boundingBoxForRadius(center, radiusMeters) {
    const METERS_PER_DEGREE_LAT = 111320;
    const latDelta = radiusMeters / METERS_PER_DEGREE_LAT;
    const lngDelta = radiusMeters / (METERS_PER_DEGREE_LAT * Math.cos((center.lat * Math.PI) / 180));
    return {
      north: center.lat + latDelta,
      south: center.lat - latDelta,
      east: center.lng + lngDelta,
      west: center.lng - lngDelta
    };
  }

  // Fetches Google Places autocomplete predictions for free-text area input
  // (e.g. typing "New" suggests "New Okhla Industrial Development Area",
  // "New Ashok Nagar", "New Delhi", etc.), restricted to India. When the user's
  // location is known (detected automatically or picked from the city dropdown),
  // results are further restricted to that specific city only.
  async function fetchPlaceAutocompleteSuggestions(inputText) {
    try {
      const places = await getPlacesLibrary();
      if (!autocompleteSessionToken) {
        autocompleteSessionToken = new places.AutocompleteSessionToken();
      }
      const request = {
        input: inputText,
        sessionToken: autocompleteSessionToken,
        includedRegionCodes: ['in']
      };
      const restrictionCenter = getCityRestrictionCenter();
      if (restrictionCenter) {
        request.locationRestriction = boundingBoxForRadius(restrictionCenter, CITY_SUGGESTION_RADIUS_METERS);
      }
      const { suggestions } = await withTimeout(
        places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request),
        GEOCODE_TIMEOUT_MS
      );
      return (suggestions || []).map((s) => s.placePrediction).filter(Boolean).slice(0, 5);
    } catch (error) {
      console.error('Error fetching place suggestions:', error);
      return [];
    }
  }

  // Resolve a picked Places suggestion straight to coordinates via the Places
  // library (more precise than re-geocoding its text, and avoids the
  // ambiguous-name mismatches free-text search can hit).
  async function resolvePlaceSuggestion(prediction, displayText) {
    areaSearchSubmitted = true;
    const requestId = ++locationRequestId;
    autocompleteSessionToken = null; // this session is ending
    showLocationLoading(`Finding stores near "${displayText}"…`);

    try {
      const place = prediction.toPlace();
      await withTimeout(place.fetchFields({ fields: ['location', 'formattedAddress'] }), GEOCODE_TIMEOUT_MS);
      if (requestId !== locationRequestId) return; // superseded by a newer request

      if (place.location) {
        setUserCoords(place.location.lat(), place.location.lng());
        filteredStores = sortStoresByDistance([...allStoresData]);
        currentPage = 1;
        updateURLParameter('page', '1');
        notifyLocationOutcome(false, place.formattedAddress || displayText);
        renderStores();
        return;
      }
    } catch (error) {
      console.error('Error resolving place suggestion:', error);
    }

    // Fall back to the normal literal-match + geocode-text flow
    performAreaSearch();
  }

  // Read the user's known coordinates (set via geolocation or pincode geocoding)
  function getUserCoords() {
    const lat = parseFloat(sessionStorage.getItem('userLat'));
    const lng = parseFloat(sessionStorage.getItem('userLng'));
    if (isNaN(lat) || isNaN(lng)) return null;
    return { lat, lng };
  }

  function setUserCoords(lat, lng) {
    if (typeof lat !== 'number' || typeof lng !== 'number' || isNaN(lat) || isNaN(lng)) return;
    sessionStorage.setItem('userLat', String(lat));
    sessionStorage.setItem('userLng', String(lng));
  }

  // Great-circle distance between two lat/lng points, in kilometres
  function haversineDistanceKm(lat1, lon1, lat2, lon2) {
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  // Sort stores nearest-first using the user's known coordinates, when available.
  // Stores missing latitude/longitude data sort to the end rather than being dropped.
  function sortStoresByDistance(stores) {
    const userCoords = getUserCoords();
    if (!userCoords) return stores;

    return stores
      .map((store, index) => {
        const storeLat = parseFloat(store.latitude);
        const storeLng = parseFloat(store.longitude);
        const hasCoords = !isNaN(storeLat) && !isNaN(storeLng);
        const distanceKm = hasCoords
          ? haversineDistanceKm(userCoords.lat, userCoords.lng, storeLat, storeLng)
          : Infinity;
        return { store, index, distanceKm };
      })
      .sort((a, b) => {
        if (a.distanceKm !== b.distanceKm) return a.distanceKm - b.distanceKm;
        return a.index - b.index; // stable fallback for equal/unknown distance
      })
      .map((entry) => entry.store);
  }

  // Forward-geocode a pincode to a city name + coordinates via the Maps JS API Geocoder.
  // Cached per-pincode since Geocoding calls are billed.
  async function fetchCityFromPincode(pincode) {
    if (!pincode) return null;

    if (pincodeGeocodeCache.has(pincode)) {
      const cached = pincodeGeocodeCache.get(pincode);
      setUserCoords(cached.lat, cached.lng);
      return cached.city;
    }

    try {
      const geocoder = await getGeocoder();
      const { results } = await withTimeout(geocoder.geocode({
        address: pincode,
        componentRestrictions: { country: 'IN' }
      }), GEOCODE_TIMEOUT_MS);

      if (!results || !results.length) return null;

      const location = results[0].geometry && results[0].geometry.location;
      const lat = location ? location.lat() : null;
      const lng = location ? location.lng() : null;
      const city = extractAddressComponent(results, ['locality', 'postal_town', 'administrative_area_level_2']);
      const mappedCity = city ? getMappedCity(city) : null;

      if (lat != null && lng != null) {
        setUserCoords(lat, lng);
      }
      if (mappedCity) {
        pincodeGeocodeCache.set(pincode, { city: mappedCity, lat, lng });
      }

      return mappedCity;
    } catch (error) {
      console.error('Error fetching city from pincode:', error);
      if (window.ZiglyCapture) {
        window.ZiglyCapture(error, { feature: 'serviceability', tags: { area: 'store_locator' } });
      }
      return null;
    }
  }

  // Geocode arbitrary free-text area/locality input (e.g. "Jasola Vihar") to
  // coordinates, so a search for an area with no store of its own can still
  // fall back to showing the nearest stores instead of an empty result.
  async function geocodeAreaText(areaText) {
    const key = areaText.trim().toLowerCase();
    if (!key) return null;
    if (areaGeocodeCache.has(key)) return areaGeocodeCache.get(key);

    try {
      const geocoder = await getGeocoder();
      const { results } = await withTimeout(geocoder.geocode({
        address: areaText,
        componentRestrictions: { country: 'IN' }
      }), GEOCODE_TIMEOUT_MS);

      if (!results || !results.length) {
        areaGeocodeCache.set(key, null);
        return null;
      }

      const location = results[0].geometry && results[0].geometry.location;
      const coords = location
        ? { lat: location.lat(), lng: location.lng(), formattedAddress: results[0].formatted_address || null }
        : null;
      areaGeocodeCache.set(key, coords);
      return coords;
    } catch (error) {
      console.error('Error geocoding area text:', error);
      if (window.ZiglyCapture) {
        window.ZiglyCapture(error, { feature: 'serviceability', tags: { area: 'store_locator' } });
      }
      return null;
    }
  }

  // Show error message
  function showErrorMessage() {
    if (errorMessage) {
      errorMessage.style.display = 'block';
    }

    if (errorMessageTimer) {
      clearTimeout(errorMessageTimer);
    }

    if (shouldScrollToError && errorMessage) {
      scrollToErrorMessage();
    }
    shouldScrollToError = false;

    errorMessageTimer = setTimeout(() => {
      hideErrorMessage();
    }, 3000);
  }

  // Hide error message
  function hideErrorMessage() {
    if (errorMessage) {
      errorMessage.style.display = 'none';
    }

    if (errorMessageTimer) {
      clearTimeout(errorMessageTimer);
      errorMessageTimer = null;
    }
  }

  function scrollToErrorMessage() {
    if (!errorMessage) return;
    const errorTop = errorMessage.getBoundingClientRect().top + window.pageYOffset;
    const scrollPosition = errorTop - 150;
    window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
  }

  // Show/hide the neutral "showing stores nearest to your location" banner -
  // used instead of the red error when we have real coordinates but couldn't
  // confirm a served city name for them (e.g. reverse-geocoding failed/timed out).
  //
  // resolvedLabel, when given, names the place the geocoder actually matched
  // (e.g. "Vasundhra, Pilibhit, Uttar Pradesh 262001, India"). Free-text area
  // search is ambiguous - a misspelling can silently match a real but
  // unrelated place - so surfacing exactly what got matched lets a mismatch
  // like that be spotted immediately instead of just looking broken.
  function showInfoMessage(resolvedLabel) {
    if (infoMessage) {
      infoMessage.style.display = 'block';
      infoMessage.textContent = resolvedLabel
        ? `Showing stores nearest to "${resolvedLabel}".`
        : 'Showing stores nearest to your location.';
    }
    shouldScrollToError = false; // this banner is informational, never scrolls the page

    // Reflect the "near you" state in the dropdown trigger too, so it doesn't
    // sit on the generic "Search Location" placeholder while results are
    // actually filtered to the user's location - that reads as broken/confusing.
    const triggerText = document.querySelector('.store-locator__city-trigger-text');
    if (triggerText) {
      triggerText.textContent = 'Near your location';
    }
  }

  function hideInfoMessage() {
    if (infoMessage) {
      infoMessage.style.display = 'none';
    }
  }

  // Shown while detecting location / geocoding (GPS lookup, area/place search),
  // so the page never looks frozen during that 1-6s wait. Replaces the store
  // grid; always cleared by the next renderStores() call regardless of outcome.
  function showLocationLoading(text) {
    if (loadingText && text) {
      loadingText.textContent = text;
    }
    if (loadingState) {
      loadingState.style.display = 'flex';
    }
    if (storeContainer) {
      storeContainer.style.display = 'none';
    }
    if (noResultsMessage) {
      noResultsMessage.style.display = 'none';
    }
    if (paginationContainer) {
      paginationContainer.style.display = 'none';
    }
  }

  function hideLocationLoading() {
    if (loadingState) {
      loadingState.style.display = 'none';
    }
  }

  // Single place that decides which (if either) of the error/info banners to
  // show once a city-resolution attempt has finished. Coordinates being known
  // (even without a confirmed city name) is enough for the store list to be
  // usefully sorted nearest-first, so that case gets the friendly info banner
  // instead of the "we don't serve this area" error.
  function notifyLocationOutcome(cityResolved, resolvedLabel) {
    if (cityResolved) {
      hideErrorMessage();
      hideInfoMessage();
      updateLocationBanner(resolvedLabel);
      return;
    }
    if (getUserCoords()) {
      hideErrorMessage();
      showInfoMessage(resolvedLabel);
      updateLocationBanner(resolvedLabel);
    } else {
      hideInfoMessage();
      showErrorMessage();
      updateLocationBanner(null);
    }
  }

  // Well-known NCR-area cities/localities that should show as the single
  // grouped "Delhi NCR" label rather than whichever one specifically resolved.
  const NCR_KEYWORDS = ['delhi', 'noida', 'gurugram', 'gurgaon', 'ghaziabad', 'faridabad'];

  // Turns whatever label a resolution flow has on hand (a clean city name, or
  // a full geocoded formatted address) into a short label for the banner.
  function areaLabelForBanner(rawLabel) {
    if (!rawLabel) return null;
    const lower = rawLabel.toLowerCase();
    if (NCR_KEYWORDS.some((keyword) => lower.includes(keyword))) return 'Delhi NCR';

    // A full formatted address ("X, City, State, India") usually has the city
    // as the second-to-last comma-separated segment.
    const parts = rawLabel.split(',').map((p) => p.trim()).filter(Boolean);
    if (parts.length >= 2) return parts[parts.length - 2];
    return parts[0] || rawLabel;
  }

  // The "Search by Area/Center" row only makes sense once we know roughly
  // where the customer is - either their location was detected (geolocation
  // or pincode, giving us coordinates) or they manually picked a city from
  // the dropdown. Otherwise there's nothing to scope the area search to, so
  // it stays hidden and only the city dropdown shows.
  function isLocationKnown() {
    return Boolean(getUserCoords() || lastSelectedCity);
  }

  function updateAreaSearchVisibility() {
    const shouldShow = isLocationKnown();
    if (areaSearchWrapper) areaSearchWrapper.style.display = shouldShow ? '' : 'none';
    if (searchButton) searchButton.style.display = shouldShow ? '' : 'none';
    // The dropdown's width/divider rules assume both fields are visible and
    // split the bar between them - when the area field is hidden, let the
    // dropdown fill the whole bar instead of leaving empty space beside it.
    if (searchRow) {
      searchRow.classList.toggle('store-locator__search-row--single-field', !shouldShow);
    }
  }

  // Switches the "Enable location" banner between its prompt state (no
  // location known yet) and its confirmation state ('Showing centers in
  // "X"'), persisting the resolved label so a same-session page reload can
  // restore the confirmation state immediately without re-resolving.
  function updateLocationBanner(rawLabel) {
    updateAreaSearchVisibility();

    if (!locationBanner) return;

    if (!getUserCoords()) {
      if (locationBannerText) locationBannerText.textContent = 'Enable location to find nearby centres';
      if (locationBannerBtn) locationBannerBtn.style.display = '';
      return;
    }

    let areaLabel = areaLabelForBanner(rawLabel);
    if (areaLabel) {
      sessionStorage.setItem('userAreaLabel', areaLabel);
    } else {
      areaLabel = sessionStorage.getItem('userAreaLabel') || 'your area';
    }

    if (locationBannerText) locationBannerText.textContent = `Showing centers in "${areaLabel}"`;
    if (locationBannerBtn) locationBannerBtn.style.display = 'none';
  }

  // Initialize from pincode
  async function initializeFromPincode() {
    const userPincode = sessionStorage.getItem('userPincode');
    
    if (!userPincode || !cityInput) {
      return;
    }

    const requestId = ++locationRequestId;

    try {
      const apiCity = await fetchCityFromPincode(userPincode);
      if (requestId !== locationRequestId) return; // a newer resolution has since started - discard this stale result
      if (apiCity) {
        // apiCity is already mapped from fetchCityFromPincode, so use it directly
        const matchingCity = findMatchingCity(apiCity);

        if (matchingCity && cityInput) {
          cityInput.value = matchingCity;
          // Sync custom dropdown
          syncCustomDropdownWithSelect();
          notifyLocationOutcome(true, matchingCity);
          // Trigger change event to filter stores
          cityInput.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          // No dropdown city matched the resolved city name - keep the filter blank
          // (shows all stores, sorted nearest-first if we have coordinates)
          if (cityInput) {
            cityInput.value = '';
          }
          // Sync custom dropdown
          syncCustomDropdownWithSelect();
          notifyLocationOutcome(false);
        }
      } else {
        notifyLocationOutcome(false); // Geocoding failed entirely - fall back to distance sort if we have coords
      }
    } catch (error) {
      console.error('Error initializing from pincode:', error);
      if (requestId !== locationRequestId) return;
      notifyLocationOutcome(false);
    }
  }

  // Reset search - clear all filters and show all stores
  function resetSearch() {
    areaSearchSubmitted = false;
    lastSelectedCity = null;
    updateAreaSearchVisibility();

    // Clear city input
    if (cityInput) {
      cityInput.value = '';
      syncCustomDropdownWithSelect();
    }
    
    // Clear area input
    if (areaInput) {
      areaInput.value = '';
      // Hide area clear button
      const areaClearBtn = document.getElementById('store-locator-area-clear-btn');
      const areaInputWrapper = areaInput.closest('.store-locator__search-input-wrapper');
      if (areaClearBtn) {
        areaClearBtn.style.display = 'none';
      }
      if (areaInputWrapper) {
        areaInputWrapper.classList.remove('store-locator__area-input-has-value');
      }
    }
    
    // Hide area suggestions
    hideAreaSuggestions();

    // Hide pincode search panel, if open (syncCustomDropdownWithSelect()
    // above already reset the trigger label to match the cleared city)
    hidePincodePanel();

    // Hide error/info messages
    hideErrorMessage();
    hideInfoMessage();

    // Reset to page 1 and filter (shows all stores)
    currentPage = 1;
    updateURLParameter('page', '1');
    filterStores();
    renderStores();
  }


  // Filter stores based on city and area
  function filterStores(preservePage) {
    const cityQuery = cityInput ? cityInput.value.trim() : '';
    const areaQuery = areaInput ? areaInput.value.trim().toLowerCase() : '';

    if (areaQuery !== '') {
      // Area/Store name search - filter by address OR store name
      filteredStores = allStoresData.filter(store => {
        const address = (store.address || '').toLowerCase();
        const storeName = (store.name || '').toLowerCase();
        return address.includes(areaQuery) || storeName.includes(areaQuery);
      });
    } else if (cityQuery !== '') {
      // City search - filter by exact city match (case-insensitive)
      filteredStores = allStoresData.filter(store => {
        const city = (store.city || '').trim();
        return city.toLowerCase() === cityQuery.toLowerCase();
      });
    } else {
      // No filter - show all stores
      filteredStores = [...allStoresData];
    }

    // Order nearest-to-farthest when we know the user's pincode
    filteredStores = sortStoresByDistance(filteredStores);

    // Reset to page 1 after filtering (unless preserving page for URL)
    if (!preservePage) {
      currentPage = 1;
      updateURLParameter('page', '1');
    }
  }

  // Runs a full area search: literal address/name match first (filterStores),
  // and if that finds nothing, geocodes the typed area text itself (e.g. an
  // area like "Jasola Vihar" with no store of its own) and falls back to
  // showing every store sorted nearest to that area instead of "no results".
  async function performAreaSearch() {
    areaSearchSubmitted = true;
    filterStores();

    const areaQuery = areaInput ? areaInput.value.trim() : '';
    if (areaQuery && filteredStores.length === 0) {
      const requestId = ++locationRequestId;
      showLocationLoading(`Finding stores near "${areaQuery}"…`);
      const coords = await geocodeAreaText(areaQuery);
      if (requestId !== locationRequestId) return; // superseded by a newer location request

      if (coords) {
        setUserCoords(coords.lat, coords.lng);
        filteredStores = sortStoresByDistance([...allStoresData]);
        currentPage = 1;
        updateURLParameter('page', '1');
        notifyLocationOutcome(false, coords.formattedAddress); // no confirmed city, but we do have coordinates now
      }
    }

    renderStores();
  }

  // Get or create store card
  function getOrCreateStoreCard(store) {
    const storeId = store.id || store.handle || store.name;
    if (storeCardsMap.has(storeId)) {
      return storeCardsMap.get(storeId);
    }
    const card = createStoreCard(store);
    storeCardsMap.set(storeId, card);
    return card;
  }

  // Render stores for current page
  function renderStores() {
    if (!storeContainer) return;
    hideLocationLoading(); // any render means the wait is over, one way or another

    // Calculate pagination
    const totalStores = filteredStores.length;
    const totalPages = Math.ceil(totalStores / STORES_PER_PAGE);
    const startIndex = (currentPage - 1) * STORES_PER_PAGE;
    const endIndex = startIndex + STORES_PER_PAGE;
    const storesToRender = filteredStores.slice(startIndex, endIndex);

    if (storesToRender.length === 0) {
      // Clear container and show empty state
      storeContainer.innerHTML = '';
      if (noResultsMessage) {
        noResultsMessage.style.display = 'block';

        // While the user is still typing an area with no literal match, don't show
        // the dead-end "no results" message - clicking Search would still find
        // nearby stores via the geocode fallback, so prompt for that instead.
        const isMidTypingAreaSearch = areaInput && areaInput.value.trim() !== '' && !areaSearchSubmitted;
        const textEl = noResultsMessage.querySelector('.store-locator__empty-state-text');
        if (isMidTypingAreaSearch) {
          if (textEl) textEl.textContent = `Press Search to find stores near "${areaInput.value.trim()}".`;
          if (resetButton) resetButton.style.display = 'none';
        } else {
          if (textEl) textEl.textContent = 'No stores found matching your search.';
          if (resetButton) resetButton.style.display = '';
        }
      }
      if (storeContainer) {
        storeContainer.style.display = 'none';
      }
      if (paginationContainer) {
        paginationContainer.style.display = 'none';
      }
      return;
    }

    // Hide empty state
    if (noResultsMessage) {
      noResultsMessage.style.display = 'none';
    }
    if (storeContainer) {
      storeContainer.style.display = 'grid';
    }

    // Use a DocumentFragment to minimize reflows
    const fragment = document.createDocumentFragment();
    storesToRender.forEach(store => {
      const card = getOrCreateStoreCard(store);
      fragment.appendChild(card);
    });

    // Replace current nodes with new ones (this preserves already loaded nodes)
    if (storeContainer.replaceChildren) {
      storeContainer.replaceChildren(fragment);
    } else {
      storeContainer.innerHTML = '';
      storeContainer.appendChild(fragment);
    }

    // Load Featurable script only if any store has a widget configured
    if (storesToRender.some(store => store.featurableWidgetId)) {
      ensureFeaturableScriptLoaded();
      observeFeaturableWidgets();
      retriggerFeaturable();
      // Fix error messages after a delay to catch any that appear after widget loads
      setTimeout(() => {
        fixFeaturableErrorMessages();
      }, 500);
    }

    // Render pagination
    renderPagination(totalPages);
  }

  // Create store card HTML
  function createStoreCard(store) {
    const card = document.createElement('div');
    card.className = 'store-card visible';
    card.setAttribute('data-city', store.city || '');
    card.setAttribute('data-address', (store.address || '').toLowerCase());
    card.setAttribute('data-store-id', store.id || '');

    const hasFeaturableWidget = Boolean(store.featurableWidgetId);

    // Build rating stars using SVG snippets
    const activeStarSVG = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="13" viewBox="0 0 14 13" fill="none"><path d="M6.49514 0.336808C6.65019 -0.112306 7.28536 -0.112306 7.4404 0.336809L8.75986 4.1588C8.82945 4.36038 9.01923 4.49564 9.23249 4.49564H13.4347C13.9264 4.49564 14.1228 5.13084 13.717 5.40837L10.3705 7.69673C10.1845 7.82388 10.1065 8.05969 10.1801 8.27262L11.4704 12.0104C11.6276 12.4657 11.1132 12.8582 10.7156 12.5863L7.25 10.2165C7.07984 10.1001 6.85571 10.1001 6.68555 10.2165L3.21997 12.5863C2.82231 12.8582 2.30791 12.4657 2.46511 12.0104L3.75549 8.27262C3.829 8.05969 3.75104 7.82388 3.56509 7.69673L0.218578 5.40837C-0.187276 5.13084 0.00913632 4.49564 0.500805 4.49564H4.70306C4.91632 4.49564 5.1061 4.36038 5.17569 4.1588L6.49514 0.336808Z" fill="#FDBC2A"/></svg>';
    const inactiveStarSVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 13" fill="none"><path d="M6.49514 0.336808C6.65019 -0.112306 7.28536 -0.112306 7.4404 0.336809L8.75986 4.1588C8.82945 4.36038 9.01923 4.49564 9.23249 4.49564H13.4347C13.9264 4.49564 14.1228 5.13084 13.717 5.40837L10.3705 7.69673C10.1845 7.82388 10.1065 8.05969 10.1801 8.27262L11.4704 12.0104C11.6276 12.4657 11.1132 12.8582 10.7156 12.5863L7.25 10.2165C7.07984 10.1001 6.85571 10.1001 6.68555 10.2165L3.21997 12.5863C2.82231 12.8582 2.30791 12.4657 2.46511 12.0104L3.75549 8.27262C3.829 8.05969 3.75104 7.82388 3.56509 7.69673L0.218578 5.40837C-0.187276 5.13084 0.00913632 4.49564 0.500805 4.49564H4.70306C4.91632 4.49564 5.1061 4.36038 5.17569 4.1588L6.49514 0.336808Z" fill="#BBBBBB"/></svg>';

    let ratingStars = '';
    let shouldShowRating = false;

    // Only show rating stars when there is an actual rating value
    if (store.rating) {
      const ratingNum = parseFloat(store.rating) || 0;
      const fullStars = Math.floor(ratingNum);
      shouldShowRating = true;

      for (let i = 1; i <= 5; i++) {
        if (i <= fullStars) {
          ratingStars += `<span class="store-card__star">${activeStarSVG}</span>`;
        } else {
          ratingStars += `<span class="store-card__star empty">${inactiveStarSVG}</span>`;
        }
      }
    }

    // Also consider reviewCount – if neither rating nor review count is present, hide rating block entirely
    if (!store.rating && !store.reviewCount) {
      shouldShowRating = false;
      ratingStars = '';
    }

    // Build service tags with check icon SVG
    const checkIconSVG = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 0C2.6863 0 0 2.6863 0 6C0 9.3137 2.6863 12 6 12C9.3137 12 12 9.3137 12 6C12 2.6863 9.31369 0 6 0ZM6 1.30371C8.59369 1.30371 10.6956 3.40696 10.6956 6C10.6956 8.59304 8.59369 10.6956 6 10.6956C3.4063 10.6956 1.30444 8.59305 1.30444 6C1.30445 3.40696 3.4063 1.30371 6 1.30371ZM8.29907 3.15088L4.82153 6.62915L3.6936 5.50122L2.58691 6.60718L3.71484 7.73511L4.82885 8.84912L5.93482 7.74243L9.41308 4.26489L8.29907 3.15088Z" fill="#009824"/></svg>';
    
    let serviceTagsHtml = '';
    if (store.serviceTags) {
      const tags = Array.isArray(store.serviceTags) ? store.serviceTags : store.serviceTags.split(',');
      tags.forEach(tag => {
        const cleanTag = tag.trim();
        if (cleanTag) {
          serviceTagsHtml += `<div class="store-card__service"><span class="store-card__service-icon">${checkIconSVG}</span><span>${cleanTag}</span></div>`;
        }
      });
    }

    // Build open timing text
    let openTimingText = '';
    if (store.openTiming === '00:00 - 23:59' || store.openTiming === '24 Hours' || store.openDays === 'All Days') {
      openTimingText = '(Open 24 Hours)';
    } else if (store.openTiming) {
      openTimingText = `(${store.openTiming})`;
    } else if (store.openDays) {
      openTimingText = `(${store.openDays})`;
    }

    // Build location name (city - area)
    let locationName = '';
    if (store.city) {
      const addressParts = store.address ? store.address.split(',') : [];
      const area = addressParts.length > 0 ? addressParts[0].trim() : '';
      locationName = area ? `${store.city} - ${area}` : store.city;
    } else if (store.address) {
      locationName = store.address.split(',')[0].trim();
    }

    // Build store hours text
    let storeHours = '';
    if (store.openDays) {
      if (store.openDays === '00:00 - 23:59' || store.openDays === '24 Hours' || store.openDays === 'All Days') {
        storeHours = 'Open All Days :- 00:00 – 23:59';
      } else if (store.openDays) {
        storeHours = `${store.openDays}`;
      } else {
        storeHours = `Open All Days :- ${store.openDays}`;
      }
    }

    // Build action buttons
    const directionUrl = store.locationUrl || '#';
    const detailsUrl = store.storeDetailsUrl || store.handle || store.id || '';
    const appointmentUrl = store.storeDetailsUrl || store.handle || store.id || '';
    let reviewUrl = store.reviewLink || '';
    if (reviewUrl && !reviewUrl.startsWith('http') && !reviewUrl.startsWith('//')) {
      reviewUrl = `https://search.google.com/local/writereview?placeid=${reviewUrl}`;
    }

    const writeReviewSVG = `<svg xmlns="http://www.w3.org/2000/svg" width="512" height="512" viewBox="0 0 512 512">
      <g>
      <path d="M 184.14 488.75 C167.88,496.32 147.57,488.29 139.66,471.18 C137.64,466.80 137.52,465.22 137.78,446.35 L 138.06 426.19 L 107.28 425.75 C72.75,425.26 68.69,424.63 55.46,417.67 C41.11,410.13 30.81,399.37 23.99,384.80 C23.15,383.00 22.42,381.49 21.80,379.95 C17.93,370.37 17.96,359.21 18.20,265.97 C18.21,263.60 18.22,261.17 18.22,258.69 L 18.50 153.50 L 20.84 146.50 C26.68,129.02 39.66,113.70 55.01,106.17 C58.17,104.62 60.48,103.38 62.88,102.40 C72.42,98.48 83.56,98.48 156.23,98.50 C161.39,98.50 166.87,98.50 172.68,98.50 C268.38,98.50 268.87,98.51 271.68,100.57 C275.15,103.12 278.00,108.49 278.00,112.50 C278.00,116.83 275.02,122.11 271.10,124.73 L 267.70 127.00 L 175.99 127.00 C118.14,127.00 82.28,127.37 78.89,128.02 C66.75,130.30 55.96,138.68 50.59,150.00 L 47.50 156.50 L 47.50 367.50 L 50.18 373.22 C53.50,380.30 60.41,387.82 66.80,391.30 C76.57,396.61 80.22,397.00 120.46,397.00 C155.43,397.01 157.68,397.12 160.70,398.96 C167.12,402.87 167.38,404.42 166.62,433.64 C166.01,456.90 166.11,459.97 167.54,461.40 C168.42,462.28 169.87,463.00 170.76,463.00 C171.65,463.00 191.05,448.94 213.87,431.75 C236.69,414.56 256.74,399.73 258.43,398.79 C261.21,397.24 266.33,397.08 312.70,397.04 C346.64,397.01 365.69,396.62 369.20,395.88 C381.30,393.31 391.89,385.09 397.28,374.09 L 400.50 367.50 L 401.00 302.00 C401.47,240.09 401.60,236.33 403.39,233.39 C406.07,228.96 410.31,226.77 416.21,226.77 C422.06,226.77 425.61,228.92 428.27,234.07 C429.92,237.27 430.02,242.00 429.77,303.50 L 429.50 369.50 L 426.88 377.17 C422.93,388.74 417.50,397.61 409.54,405.47 C398.22,416.67 385.39,422.97 369.78,424.99 C365.78,425.51 341.56,425.95 315.96,425.97 L 269.43 426.00 L 229.46 456.13 C207.48,472.70 187.09,487.38 184.14,488.75 ZM 379.11 196.96 C356.70,205.91 353.60,206.43 345.50,202.63 C339.69,199.91 335.57,196.04 332.90,190.80 C330.64,186.38 330.46,178.47 332.09,156.05 L 333.19 141.06 L 320.57 126.04 C305.82,108.50 304.13,105.87 303.36,99.36 C302.20,89.59 307.28,79.89 315.74,75.67 C318.36,74.37 328.38,71.38 338.00,69.02 C347.62,66.66 356.26,64.34 357.20,63.86 C358.13,63.38 363.28,55.90 368.64,47.24 C373.99,38.58 379.99,29.86 381.97,27.85 C385.59,24.17 393.48,21.00 399.00,21.00 C403.41,21.00 411.73,23.90 414.86,26.53 C416.48,27.90 422.69,36.86 428.65,46.44 L 439.50 63.86 L 460.27 69.06 C483.86,74.96 486.75,76.44 491.44,85.06 C493.77,89.35 494.37,91.69 494.36,96.48 C494.33,104.54 492.17,108.48 479.67,123.22 C474.08,129.82 468.42,136.57 467.10,138.21 L 464.69 141.21 L 465.86 157.85 C467.90,186.93 467.45,189.88 459.76,197.73 C454.69,202.92 447.77,205.50 441.12,204.69 C438.58,204.38 428.11,200.70 417.87,196.52 L 399.23 188.92 ZM 360.37 165.35 L 359.71 173.93 L 375.61 167.38 C394.82,159.45 397.45,158.64 401.41,159.42 C403.11,159.76 411.83,163.01 420.79,166.64 C429.75,170.28 437.32,173.02 437.61,172.73 C437.90,172.44 437.65,166.22 437.07,158.90 C435.86,143.80 435.70,132.95 436.65,130.47 C437.01,129.54 442.85,122.22 449.63,114.20 C456.42,106.19 461.83,99.50 461.66,99.33 C461.49,99.16 453.96,97.20 444.93,94.97 C435.89,92.74 427.27,90.44 425.78,89.85 C421.42,88.14 418.70,84.77 408.82,68.92 C403.71,60.72 399.26,54.00 398.93,54.00 C398.61,54.00 393.72,61.44 388.07,70.53 C379.69,84.02 377.13,87.40 374.15,88.88 C372.14,89.89 362.73,92.63 353.23,94.98 C337.78,98.80 336.09,99.42 337.23,100.86 C337.93,101.74 343.38,108.30 349.35,115.42 C355.31,122.54 360.65,129.52 361.20,130.94 C362.18,133.41 361.88,145.69 360.37,165.35 ZM 313.11 237.82 L 309.23 240.00 L 227.35 240.00 C149.58,240.00 145.29,239.91 141.98,238.15 C133.65,233.72 131.26,223.19 137.08,216.56 C138.81,214.59 141.77,212.60 143.83,212.03 C146.18,211.38 176.52,211.01 228.28,211.01 C296.33,211.00 309.61,211.23 312.51,212.44 C316.29,214.02 319.66,218.71 320.60,223.72 C321.50,228.46 317.98,235.08 313.11,237.82 ZM 237.73 311.04 C233.77,312.84 230.76,312.96 190.00,312.96 C149.24,312.96 146.23,312.84 142.27,311.04 C131.62,306.20 131.09,290.51 141.42,285.61 C144.37,284.20 150.56,284.00 189.93,284.00 C239.80,284.00 240.23,284.05 243.97,290.19 C248.49,297.60 245.52,307.50 237.73,311.04 Z" fill="rgb(0,0,0)"/>
      <path d="M 0.00 256.00 L 0.00 0.00 L 256.00 0.00 L 512.00 0.00 L 512.00 256.00 L 512.00 512.00 L 256.00 512.00 L 0.00 512.00 L 0.00 256.00 ZM 184.14 488.75 C187.09,487.38 207.48,472.70 229.46,456.13 L 269.43 426.00 L 315.96 425.97 C341.56,425.95 365.78,425.51 369.78,424.99 C385.39,422.97 398.22,416.67 409.54,405.47 C417.50,397.61 422.93,388.74 426.88,377.17 L 429.50 369.50 L 429.77 303.50 C430.02,242.00 429.92,237.27 428.27,234.07 C425.61,228.92 422.06,226.77 416.21,226.77 C410.31,226.77 406.07,228.96 403.39,233.39 C401.60,236.33 401.47,240.09 401.00,302.00 L 400.50 367.50 L 397.28 374.09 C391.89,385.09 381.30,393.31 369.20,395.88 C365.69,396.62 346.64,397.01 312.70,397.04 C266.33,397.08 261.21,397.24 258.43,398.79 C256.74,399.73 236.69,414.56 213.87,431.75 C191.05,448.94 171.65,463.00 170.76,463.00 C169.87,463.00 168.42,462.28 167.54,461.40 C166.11,459.97 166.01,456.90 166.62,433.64 C167.38,404.42 167.12,402.87 160.70,398.96 C157.68,397.12 155.43,397.01 120.46,397.00 C80.22,397.00 76.57,396.61 66.80,391.30 C60.41,387.82 53.50,380.30 50.18,373.22 L 47.50 367.50 L 47.50 262.00 L 47.50 156.50 L 50.59 150.00 C55.96,138.68 66.75,130.30 78.89,128.02 C82.28,127.37 118.14,127.00 175.99,127.00 L 267.70 127.00 L 271.10 124.73 C275.02,122.11 278.00,116.83 278.00,112.50 C278.00,108.49 275.15,103.12 271.68,100.57 C268.87,98.51 268.38,98.50 172.68,98.50 C64.54,98.50 71.60,98.04 55.01,106.17 C39.66,113.70 26.68,129.02 20.84,146.50 L 18.50 153.50 L 18.22 258.69 C17.92,374.37 17.79,371.54 23.99,384.80 C30.81,399.37 41.11,410.13 55.46,417.67 C68.69,424.63 72.75,425.26 107.28,425.75 L 138.06 426.19 L 137.78 446.35 C137.52,465.22 137.64,466.80 139.66,471.18 C147.57,488.29 167.88,496.32 184.14,488.75 ZM 237.73 311.04 C245.52,307.50 248.49,297.60 243.97,290.19 C240.23,284.05 239.80,284.00 189.93,284.00 C150.56,284.00 144.37,284.20 141.42,285.61 C131.09,290.51 131.62,306.20 142.27,311.04 C146.23,312.84 149.24,312.96 190.00,312.96 C230.76,312.96 233.77,312.84 237.73,311.04 ZM 313.11 237.82 C317.98,235.08 321.50,228.46 320.60,223.72 C319.66,218.71 316.29,214.02 312.51,212.44 C309.61,211.23 296.33,211.00 228.28,211.01 C176.52,211.01 146.18,211.38 143.83,212.03 C141.77,212.60 138.81,214.59 137.08,216.56 C131.26,223.19 133.65,233.72 141.98,238.15 C145.29,239.91 149.58,240.00 227.35,240.00 L 309.23 240.00 L 313.11 237.82 ZM 379.11 196.96 L 399.23 188.92 L 417.87 196.52 C428.11,200.70 438.58,204.38 441.12,204.69 C447.77,205.50 454.69,202.92 459.76,197.73 C467.45,189.88 467.90,186.93 465.86,157.85 L 464.69 141.21 L 467.10 138.21 C468.42,136.57 474.08,129.82 479.67,123.22 C492.17,108.48 494.33,104.54 494.36,96.48 C494.37,91.69 493.77,89.35 491.44,85.06 C486.75,76.44 483.86,74.96 460.27,69.06 L 439.50 63.86 L 428.65 46.44 C422.69,36.86 416.48,27.90 414.86,26.53 C411.73,23.90 403.41,21.00 399.00,21.00 C393.48,21.00 385.59,24.17 381.97,27.85 C379.99,29.86 373.99,38.58 368.64,47.24 C363.28,55.90 358.13,63.38 357.20,63.86 C356.26,64.34 347.62,66.66 338.00,69.02 C328.38,71.38 318.36,74.37 315.74,75.67 C307.28,79.89 302.20,89.59 303.36,99.36 C304.13,105.87 305.82,108.50 320.57,126.04 L 333.19 141.06 L 332.09 156.05 C330.46,178.47 330.64,186.38 332.90,190.80 C335.57,196.04 339.69,199.91 345.50,202.63 C353.60,206.43 356.70,205.91 379.11,196.96 ZM 360.37 165.35 C361.88,145.69 362.18,133.41 361.20,130.94 C360.65,129.52 355.31,122.54 349.35,115.42 C343.38,108.30 337.93,101.74 337.23,100.86 C336.09,99.42 337.78,98.80 353.23,94.98 C362.73,92.63 372.14,89.89 374.15,88.88 C377.13,87.40 379.69,84.02 388.07,70.53 C393.72,61.44 398.61,54.00 398.93,54.00 C399.26,54.00 403.71,60.72 408.82,68.92 C418.70,84.77 421.42,88.14 425.78,89.85 C427.27,90.44 435.89,92.74 444.93,94.97 C453.96,97.20 461.49,99.16 461.66,99.33 C461.83,99.50 456.42,106.19 449.63,114.20 C442.85,122.22 437.01,129.54 436.65,130.47 C435.70,132.95 435.86,143.80 437.07,158.90 C437.65,166.22 437.90,172.44 437.61,172.73 C437.32,173.02 429.75,170.28 420.79,166.64 C411.83,163.01 403.11,159.76 401.41,159.42 C397.45,158.64 394.82,159.45 375.61,167.38 L 359.71 173.93 L 360.37 165.35 Z" fill="none"/>
      </g>
      </svg>`
    const reviewLinkHtml = reviewUrl ? `<a href="${reviewUrl}" target="_blank" rel="noopener" class="store-card__write-review" title="Write a review">${writeReviewSVG}</a>` : '';

    // Build rating block HTML (desktop & mobile) only when we have rating/review data
    const ratingBlockHtml = shouldShowRating
      ? `
              <div class="store-card__rating">
                <div class="store-card__stars">${ratingStars}</div>
                <div class="store-card__count">(${store.reviewCount || '0'})</div>
                ${reviewLinkHtml}
              </div>
            `
      : '';

    // Build phone links for header
    let phoneLinks = '';
    if (store.phone) {
      // Remove all non-digit characters (spaces, hyphens, parentheses, plus signs, dots, etc.)
      const cleanPhone = store.phone.replace(/[^\d]/g, '');
      const whatsappNumber = store.whatsappNumber ? store.whatsappNumber.replace(/[^\d]/g, '') : cleanPhone;
      phoneLinks = `
      
        <a href="https://wa.me/${whatsappNumber}" target="_blank" rel="noopener" class="store-card__icon whatsapp" aria-label="WhatsApp store">
          <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="none">
          <g clip-path="url(#clip0_9422_16074)">
            <path d="M13 0.625C19.8345 0.625 25.375 6.16548 25.375 13C25.375 19.8345 19.8345 25.375 13 25.375C6.16548 25.375 0.625 19.8345 0.625 13C0.625 6.16548 6.16548 0.625 13 0.625Z" fill="#27D045" stroke="white" stroke-width="1.25"/>
            <path d="M13.1085 19.175C16.5787 19.175 19.3919 16.3619 19.3919 12.8917C19.3919 9.42148 16.5787 6.60834 13.1085 6.60834C9.63834 6.60834 6.8252 9.42148 6.8252 12.8917C6.8252 16.3619 9.63834 19.175 13.1085 19.175Z" stroke="white" stroke-width="0.75"/>
            <path d="M7.36678 16.4667L6.37012 19.9983L10.0318 18.98L10.2268 17.8533L7.84343 18.6117L8.47177 16.3367L7.36678 16.4667Z" fill="white"/>
            <path d="M7.7998 18.6333C7.7998 18.6117 8.55814 16.0983 8.55814 16.0983L10.3781 17.81L7.7998 18.6333Z" fill="#27D045"/>
            <path d="M10.3931 9.77879C10.1718 9.9323 9.98323 10.1281 9.83819 10.355C9.69315 10.5819 9.59454 10.8352 9.54805 11.1005C9.43972 11.9455 9.95972 12.8121 9.95972 12.8121C10.4872 13.5969 11.1134 14.3105 11.8231 14.9355C12.4438 15.4266 13.1485 15.8011 13.903 16.0405C14.3432 16.2162 14.8246 16.2613 15.2897 16.1705C15.702 16.0573 16.0667 15.8141 16.3297 15.4771C16.3913 15.3238 16.435 15.1638 16.4597 15.0005C16.4845 14.8571 16.4845 14.7105 16.4597 14.5671C16.4381 14.4155 14.8781 13.7005 14.7264 13.7438C14.5747 13.7871 14.4447 14.0688 14.2714 14.3071C14.098 14.5455 14.0331 14.6538 13.8597 14.6538C13.6864 14.6538 12.928 14.3505 12.213 13.7005C11.8046 13.3463 11.4855 12.901 11.2814 12.4005C11.2814 12.4005 11.4114 12.2488 11.5847 12.0105C11.7196 11.8627 11.8163 11.6842 11.8664 11.4905C11.8664 11.3821 11.2597 9.84379 11.108 9.77879C10.8709 9.74545 10.6302 9.74545 10.3931 9.77879Z" fill="white"/>
            </g>
            <defs>
            <clipPath id="clip0_9422_16074">
            <rect width="26" height="26" fill="white"/>
            </clipPath>
            </defs>
          </svg>
        </a>
      `;
    }

    // Build image HTML
    let imageHtml = '';
    if (store.imageUrl) {
      const imageUrl = store.imageUrl;
      imageHtml = `
        <div class="store-card__image-wrapper">
          <img src="${imageUrl}" alt="${locationName || store.name || 'Store'}" class="store-card__image" loading="lazy" width="180" height="220"
      decoding="async"
      fetchpriority="low">
        </div>
      `;
    } else {
      imageHtml = `
        <div class="store-card__image-wrapper">
          <div class="store-card__image" style="background: #f0f0f0; display: flex; align-items: center; justify-content: center; color: #999;">
            Store Image
          </div>
        </div>
      `;
    }

    // Build Featurable reviews widget (per store)
    let reviewsHtml = '';
    if (store.featurableWidgetId) {
      const widgetId = String(store.featurableWidgetId).trim();
      if (widgetId) {
        reviewsHtml = `
         
        `;
      }
    }

    // Build address text
    const addressText = store.address || '';

    // Build full card HTML using 6-grid structure
    card.innerHTML = `
      <div class="store-card__wrapper">
        <div class="store-card__main">
          ${imageHtml}
          <div class="store-card__content">
            <div class="store-card__header">
              <div class="store-card__header-info">
                <div class="store-card__title">${store.name}</div>
                ${openTimingText ? `<div class="store-card__status" style="display: none;">${openTimingText}</div>` : ''}
              </div>
              ${phoneLinks ? `<div class="store-card__icons">${phoneLinks}</div>` : ''}
            </div>

            <div class="store-card__address-mobile">
              <svg class="store-card__address-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M12 13C10.343 13 9 11.657 9 10C9 8.343 10.343 7 12 7C13.657 7 15 8.343 15 10C15 11.657 13.657 13 12 13Z" stroke="#183761" stroke-width="1.02857" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M12 21C12 21 5 15.25 5 10C5 6.134 8.134 3 12 3C15.866 3 19 6.134 19 10C19 15.25 12 21 12 21Z" stroke="#183761" stroke-width="1.02857" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
              <span>${addressText}</span>
           
            </div>
<div class="store-card__mobile-rating">
  ${ratingBlockHtml}
</div>
            <div class="store-card__body">
              <div class="store-card__address">
              <svg class="store-card__address-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M12 13C10.343 13 9 11.657 9 10C9 8.343 10.343 7 12 7C13.657 7 15 8.343 15 10C15 11.657 13.657 13 12 13Z" stroke="#183761" stroke-width="1.02857" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M12 21C12 21 5 15.25 5 10C5 6.134 8.134 3 12 3C15.866 3 19 6.134 19 10C19 15.25 12 21 12 21Z" stroke="#183761" stroke-width="1.02857" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
            
                <span>${addressText}</span>
              </div>

            
  ${ratingBlockHtml}


              ${storeHours ? `<div class="store-card__hours">${storeHours}</div>` : ''}

              ${serviceTagsHtml ? `<div class="store-card__services">${serviceTagsHtml}</div>` : ''}
            </div>
          </div>

          <div class="store-card__body-mobile">
          

            ${reviewsHtml ? `${reviewsHtml}` : ''}

            ${storeHours ? `<div class="store-card__hours">${storeHours}</div>` : ''}

            ${serviceTagsHtml ? `<div class="store-card__services">${serviceTagsHtml}</div>` : ''}
          </div>
        </div>

       <div class="store-card__actions">

  <a href="${directionUrl}" target="_blank" rel="noopener" class="store-card__btn outline-red">
    <span class="store-card__btn-icon">
    <span class="store-card__btn-icon"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5.02499 7.1296L1.51099 6.52293C1.22983 6.44446 0.980712 6.27902 0.799328 6.05031C0.617944 5.82159 0.513599 5.54134 0.50124 5.24969C0.488882 4.95804 0.569144 4.66997 0.730529 4.42673C0.891914 4.18349 1.12614 3.99757 1.39966 3.8956L10.763 0.583596C11.0105 0.493416 11.2786 0.475771 11.5358 0.532737C11.7929 0.589703 12.0285 0.718911 12.2148 0.905169C12.401 1.09143 12.5302 1.32699 12.5872 1.58417C12.6442 1.84134 12.6265 2.10944 12.5363 2.35693L9.22433 11.9023C9.12626 12.1855 8.93882 12.4293 8.69023 12.5968C8.44164 12.7644 8.14535 12.8466 7.84597 12.8312C7.54659 12.8158 7.26032 12.7035 7.03025 12.5114C6.80019 12.3192 6.63878 12.0575 6.57033 11.7656L5.96366 8.06826C5.89817 7.84579 5.77793 7.64329 5.61395 7.47931C5.44996 7.31532 5.24747 7.19508 5.02499 7.1296Z" stroke="#ED2427" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
    </span>
    <span>DIRECTION</span>
  </a>

  <a href="tel:${store.phone}" class="store-card__btn outline-blue" aria-label="Call ${store.name}">
   
 <span class="store-card__btn-icon">
  <span class="store-card__btn-icon">
  <svg class="store-detail-icon store-detail-icon--phone-button" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clip-path="url(#clip0_phone_button)">
        <path d="M14.627 15.5002C12.8908 15.5002 11.1756 15.1218 9.48112 14.3648C7.78668 13.6079 6.24501 12.535 4.85612 11.1461C3.46723 9.75719 2.39431 8.21552 1.63737 6.52108C0.880425 4.82663 0.501953 3.11136 0.501953 1.37524C0.501953 1.12524 0.585286 0.916911 0.751953 0.750244C0.91862 0.583577 1.12695 0.500244 1.37695 0.500244H4.75195C4.9464 0.500244 5.12001 0.566216 5.27279 0.698161C5.42556 0.830105 5.51584 0.986355 5.54362 1.16691L6.08529 4.08358C6.11306 4.3058 6.10612 4.4933 6.06445 4.64608C6.02279 4.79886 5.9464 4.9308 5.83529 5.04191L3.81445 7.08358C4.09223 7.59747 4.42209 8.09399 4.80404 8.57316C5.18598 9.05233 5.60612 9.51413 6.06445 9.95858C6.49501 10.3891 6.9464 10.7884 7.41862 11.1565C7.89084 11.5245 8.39084 11.8614 8.91862 12.1669L10.877 10.2086C11.002 10.0836 11.1651 9.98983 11.3665 9.92733C11.5679 9.86483 11.7658 9.84747 11.9603 9.87524L14.8353 10.4586C15.0297 10.5141 15.1895 10.6148 15.3145 10.7607C15.4395 10.9065 15.502 11.0697 15.502 11.2502V14.6252C15.502 14.8752 15.4186 15.0836 15.252 15.2502C15.0853 15.4169 14.877 15.5002 14.627 15.5002ZM3.02279 5.50024L4.39779 4.12524L4.04362 2.16691H2.18945C2.2589 2.73636 2.35612 3.29886 2.48112 3.85441C2.60612 4.40997 2.78668 4.95858 3.02279 5.50024ZM10.4811 12.9586C11.0228 13.1947 11.5749 13.3822 12.1374 13.5211C12.6999 13.66 13.2658 13.7502 13.8353 13.7919V11.9586L11.877 11.5627L10.4811 12.9586Z" fill="#183761"></path>
      </g>
      <defs>
        <clipPath id="clip0_phone_button">
          <rect width="16" height="16" fill="white"></rect>
        </clipPath>
      </defs>
    </svg></span></span>
  
  
  <span>CALL</span>
  </a>

  <a href="/pages/store-detail-page?${appointmentUrl}" class="store-card__btn solid-red store-appointment-link" data-store-handle="${appointmentUrl}">
    <span>STORE DETAILS</span>
  </a>

</div>
      </div>
    `;

    // Prevent default on dummy buttons
    const dummyButtons = card.querySelectorAll('.store-card__btn[href="#"]');
    dummyButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
      });
    });

    // Add click handlers to save store data to localStorage
    const detailsLink = card.querySelector('.store-details-link');
    const appointmentLink = card.querySelector('.store-appointment-link');
    
    const saveStoreToLocalStorage = (e) => {
      // Prevent default navigation
      e.preventDefault();
      e.stopPropagation();
      
      try {
        // Save complete store object to localStorage
        const storeData = JSON.stringify(store);
        localStorage.setItem('selectedStore', storeData);
        
        // Also save the handle for easy retrieval
        const handle = e.currentTarget.getAttribute('data-store-handle');
        if (handle) {
          localStorage.setItem('selectedStoreHandle', handle);
        }
        
        // Save store ID from metaobject (if available)
        try {
          if (store && store.storeId) {
            localStorage.setItem('store_id', store.storeId);
          }
        } catch (storeIdError) {
          // Silently continue if storeId save fails - don't block execution
          console.warn('Could not save store_id to localStorage:', storeIdError);
        }
        
        // Get the URL from the link and navigate
        const url = e.currentTarget.getAttribute('href');
        if (url) {
          // Small delay to ensure localStorage is saved
          setTimeout(() => {
            window.location.href = url;
          }, 50);
        }
      } catch (error) {
        console.error('Error saving store to localStorage:', error);
        // Still navigate even if localStorage fails
        const url = e.currentTarget.getAttribute('href');
        if (url) {
          window.location.href = url;
        }
      }
    };

    if (detailsLink) {
      detailsLink.addEventListener('click', saveStoreToLocalStorage);
    }
    
    if (appointmentLink) {
      appointmentLink.addEventListener('click', (e) => {
        // Store flag in sessionStorage to indicate user clicked "Book Appointment"
        try {
          sessionStorage.setItem('scrollToAvailableService', 'true');
        } catch (error) {
          console.error('Error saving scroll flag to sessionStorage:', error);
        }
        // Call the original handler
        saveStoreToLocalStorage(e);
      });
    }

    return card;
  }

  // Render pagination
  function renderPagination(totalPages) {
    if (!paginationContainer) return;

    if (totalPages <= 1) {
      paginationContainer.style.display = 'none';
      return;
    }

    paginationContainer.style.display = 'block';
    
    // Get pagination snippet container
    const paginationList = paginationContainer.querySelector('.store-locator__pagination-list');
    if (!paginationList) return;

    paginationList.innerHTML = '';

    // Previous button - always show, disable if on first page
    const prevLi = document.createElement('li');
    const prevDisabled = currentPage <= 1;
    const prevClass = prevDisabled ? 'store-locator__pagination-item--disabled' : '';
    prevLi.innerHTML = `
      <a href="#store-locator-section" class="store-locator__pagination-arrow ${prevClass}" aria-label="Previous page" ${prevDisabled ? 'aria-disabled="true"' : ''}>
        <div class="store-locator__pagination-arrow-circle">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="12" viewBox="0 0 14 12" fill="none">
            <path d="M12.5 6H1.5" stroke="#444249" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M6 1.5L1.5 6L6 10.5" stroke="#444249" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
      </a>
    `;
    if (!prevDisabled) {
      prevLi.querySelector('a').addEventListener('click', (e) => {
        e.preventDefault();
        goToPage(currentPage - 1);
      });
    }
    paginationList.appendChild(prevLi);

    // Page numbers - Cap to only 2 pages: 1, 2, ..., n
    if (totalPages <= 2) {
      // If 2 or fewer pages, show all pages
      for (let i = 1; i <= totalPages; i++) {
        const pageLi = createPageNumberLi(i);
        paginationList.appendChild(pageLi);
      }
    } else {
      // For 3+ pages, show: 1, 2, ..., n
      const firstLi = createPageNumberLi(1);
      paginationList.appendChild(firstLi);

      const secondLi = createPageNumberLi(2);
      paginationList.appendChild(secondLi);

      const ellipsisLi = document.createElement('li');
      ellipsisLi.innerHTML = '<span class="store-locator__pagination-ellipsis">...</span>';
      paginationList.appendChild(ellipsisLi);

      const lastLi = createPageNumberLi(totalPages);
      paginationList.appendChild(lastLi);
    }

    // Next button - always show, disable if on last page
    const nextLi = document.createElement('li');
    const nextDisabled = currentPage >= totalPages;
    const nextClass = nextDisabled ? 'store-locator__pagination-item--disabled' : '';
    nextLi.innerHTML = `
      <a href="#store-locator-section" class="store-locator__pagination-arrow ${nextClass}" aria-label="Next page" ${nextDisabled ? 'aria-disabled="true"' : ''}>
        <div class="store-locator__pagination-arrow-circle">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="12" viewBox="0 0 14 12" fill="none">
            <path d="M1.5 6H12.5" stroke="#444249" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M8 1.5L12.5 6L8 10.5" stroke="#444249" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
      </a>
    `;
    if (!nextDisabled) {
      nextLi.querySelector('a').addEventListener('click', (e) => {
        e.preventDefault();
        goToPage(currentPage + 1);
      });
    }
    paginationList.appendChild(nextLi);
  }

  // Create page number list item
  function createPageNumberLi(pageNum) {
    const li = document.createElement('li');
    const isCurrent = pageNum === currentPage;
    
    if (isCurrent) {
      li.innerHTML = `
        <a role="link" aria-disabled="true" class="store-locator__pagination-number store-locator__pagination-number--current" aria-current="page" aria-label="Page ${pageNum}, current page">
          <span class="store-locator__pagination-number-text">${pageNum}</span>
        </a>
      `;
    } else {
      li.innerHTML = `
        <a href="#store-locator-section" class="store-locator__pagination-number" aria-label="Go to page ${pageNum}">
          <span class="store-locator__pagination-number-text">${pageNum}</span>
        </a>
      `;
      li.querySelector('a').addEventListener('click', (e) => {
        e.preventDefault();
        goToPage(pageNum);
      });
    }
    
    return li;
  }

  // Go to specific page
  function goToPage(page) {
    const totalPages = Math.ceil(filteredStores.length / STORES_PER_PAGE);
    if (page < 1 || page > totalPages) return;
    
    currentPage = page;
    renderStores();
    
    // Update URL with page number
    updateURLParameter('page', page.toString());
    
    // Scroll to section with 150px offset for header
    const section = document.getElementById('store-locator-section');
    if (section) {
      const sectionTop = section.getBoundingClientRect().top + window.pageYOffset;
      const scrollPosition = sectionTop - 150;
      window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
    }
  }

  // Filter and render (debounced)
  function filterAndRender() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      requestAnimationFrame(() => {
        filterStores();
        renderStores();
      });
    }, DEBOUNCE_DELAY);
  }

  // ============================================================================
  // CUSTOM DROPDOWN FUNCTIONS
  // ============================================================================

  // Get custom dropdown elements
  function getCustomDropdownElements() {
    return {
      trigger: document.getElementById('store-locator-city-trigger'),
      menu: document.getElementById('store-locator-city-dropdown-menu'),
      closeBtn: document.getElementById('store-locator-city-close-btn'),
      wrapper: document.querySelector('.store-locator__city-dropdown-wrapper'),
      triggerText: document.querySelector('.store-locator__city-trigger-text'),
      cityItems: document.querySelectorAll('.store-locator__dropdown-item-city'),
      useLocationItem: document.querySelector('.store-locator__dropdown-item-use-location'),
      enterPincodeItem: document.querySelector('.store-locator__dropdown-item-enter-pincode'),
      searchInput: document.getElementById('store-locator-city-search'),
      cityList: document.getElementById('store-locator-city-list')
    };
  }

  // Filter cities in dropdown based on search input
  function filterCitiesInDropdown(searchTerm) {
    const elements = getCustomDropdownElements();
    if (!elements.cityItems || !elements.cityList) return;

    const searchLower = searchTerm.toLowerCase().trim();
    let visibleCount = 0;

    elements.cityItems.forEach(item => {
      const cityName = item.getAttribute('data-city-value') || '';
      const cityNameLower = cityName.toLowerCase();

      if (searchLower === '' || cityNameLower.includes(searchLower)) {
        item.style.display = 'flex';
        visibleCount++;
      } else {
        item.style.display = 'none';
      }
    });

    // Show/hide "Popular Searches" header based on visibility
    const sectionHeader = document.querySelector('.store-locator__dropdown-section-header');
    if (sectionHeader) {
      if (searchLower === '' && visibleCount > 0) {
        sectionHeader.style.display = 'block';
      } else {
        sectionHeader.style.display = 'none';
      }
    }
  }

  // Open custom dropdown
  function openCustomDropdown() {
    const elements = getCustomDropdownElements();
    if (!elements.menu || !elements.wrapper) return;

    // Reopening the dropdown means picking a different search mode - back
    // out of pincode entry (restores the trigger label) rather than leaving
    // both the panel and the dropdown menu visible at once.
    exitPincodeMode();

    elements.menu.classList.add('store-locator__city-dropdown-menu-open');
    elements.wrapper.classList.add('store-locator__city-dropdown-open');
    if (elements.trigger) {
      elements.trigger.setAttribute('aria-expanded', 'true');
    }

    // Clear search input and reset filter when opening
    if (elements.searchInput) {
      elements.searchInput.value = '';
      filterCitiesInDropdown('');
    }

    // Hide error message when opening dropdown
    hideErrorMessage();

    // Focus on search input after a short delay for animation
    setTimeout(() => {
      if (elements.searchInput) {
        elements.searchInput.focus();
      }
    }, 100);
  }

  // Close custom dropdown
  function closeCustomDropdown() {
    const elements = getCustomDropdownElements();
    if (!elements.menu || !elements.wrapper) return;

    elements.menu.classList.remove('store-locator__city-dropdown-menu-open');
    elements.wrapper.classList.remove('store-locator__city-dropdown-open');
    if (elements.trigger) {
      elements.trigger.setAttribute('aria-expanded', 'false');
    }

    // Clear search input and reset filter when closing
    if (elements.searchInput) {
      elements.searchInput.value = '';
      filterCitiesInDropdown('');
    }
  }

  // Toggle custom dropdown
  function toggleCustomDropdown() {
    const elements = getCustomDropdownElements();
    if (!elements.menu) return;

    if (elements.menu.classList.contains('store-locator__city-dropdown-menu-open')) {
      closeCustomDropdown();
    } else {
      openCustomDropdown();
    }
  }

  // Sync custom dropdown with select value
  function syncCustomDropdownWithSelect() {
    if (!cityInput) return;
    
    const selectedCity = cityInput.value;
    const elements = getCustomDropdownElements();

    // Update trigger text
    if (elements.triggerText) {
      if (selectedCity) {
        elements.triggerText.textContent = selectedCity;
      } else {
        elements.triggerText.textContent = 'Search by...';
      }
    }

    // Update selected state in dropdown
    if (elements.cityItems && elements.cityItems.length > 0) {
      elements.cityItems.forEach(item => {
        const itemCity = item.getAttribute('data-city-value');
        if (itemCity === selectedCity && selectedCity) {
          item.classList.add('store-locator__dropdown-item-selected');
        } else {
          item.classList.remove('store-locator__dropdown-item-selected');
        }
      });
    }
  }

  // Select city from custom dropdown
  function selectCityFromCustomDropdown(cityName) {
    if (!cityName) return;

    lastSelectedCity = cityName;

    // Update hidden select
    if (cityInput) {
      cityInput.value = cityName;
      // Trigger change event on select for compatibility
      const changeEvent = new Event('change', { bubbles: true });
      cityInput.dispatchEvent(changeEvent);
    }

    // Sync custom dropdown
    syncCustomDropdownWithSelect();

    // Close dropdown
    closeCustomDropdown();

    // A real city pick always wins over pincode-entry mode - trigger text
    // was already set to the city name above, so just hide the panel.
    hidePincodePanel();

    // A city was definitively resolved - clear both banners
    notifyLocationOutcome(true, cityName);

    // Clear area search
    if (areaInput) {
      areaInput.value = '';
      // Hide area clear button
      const areaClearBtn = document.getElementById('store-locator-area-clear-btn');
      const areaInputWrapper = areaInput.closest('.store-locator__search-input-wrapper');
      if (areaClearBtn) {
        areaClearBtn.style.display = 'none';
      }
      if (areaInputWrapper) {
        areaInputWrapper.classList.remove('store-locator__area-input-has-value');
      }
    }

    // Filter and render
    filterAndRender();
  }

  // Show the pincode search panel below the dropdown once "Enter Pincode" is
  // picked from it - the trigger itself stays visible and just shows "Enter
  // Pincode" as its label, mirroring how it shows a picked city's name.
  function showPincodePanel() {
    const elements = getCustomDropdownElements();
    closeCustomDropdown();
    if (elements.triggerText) elements.triggerText.textContent = 'Enter Pincode';
    if (pincodePanel) pincodePanel.style.display = 'flex';
    hidePincodeInputError();
    if (pincodeInput) {
      pincodeInput.value = '';
      pincodeInput.focus();
    }

    // Only the pincode panel should show while entering a pincode - hide the
    // "Search By Area/Center" row even if it was already visible from an
    // earlier city pick or "Use My Location" (updateAreaSearchVisibility()
    // restores it once pincode entry is left, in exitPincodeMode()).
    if (areaSearchWrapper) areaSearchWrapper.style.display = 'none';
    if (searchButton) searchButton.style.display = 'none';
    if (searchRow) searchRow.classList.add('store-locator__search-row--single-field');
  }

  // Just hides the panel - use when the trigger label is already being set
  // by whatever caused this (a resolved pincode, a picked city, "Use My
  // Location"). For backing out without submitting anything, use
  // exitPincodeMode() instead so the trigger label gets restored too.
  function hidePincodePanel() {
    if (pincodePanel) pincodePanel.style.display = 'none';
    hidePincodeInputError();
  }

  // Leaving pincode-entry mode without submitting a pincode - restores the
  // trigger label to whatever location state was actually last resolved,
  // instead of leaving it stuck on "Enter Pincode".
  function exitPincodeMode() {
    if (!pincodePanel || pincodePanel.style.display === 'none') return;
    hidePincodePanel();
    const elements = getCustomDropdownElements();
    if (elements.triggerText) {
      if (cityInput && cityInput.value) {
        elements.triggerText.textContent = cityInput.value;
      } else if (getUserCoords()) {
        elements.triggerText.textContent = 'Near your location';
      } else {
        elements.triggerText.textContent = 'Search by...';
      }
    }
    // Restore the "Search By Area/Center" row to whatever it should actually
    // show now, since showPincodePanel() force-hid it regardless of state.
    updateAreaSearchVisibility();
  }

  function showPincodeInputError(message) {
    if (pincodeInputError) {
      pincodeInputError.textContent = message;
      pincodeInputError.style.display = 'block';
    }
  }

  function hidePincodeInputError() {
    if (pincodeInputError) {
      pincodeInputError.style.display = 'none';
    }
  }

  // Handle a pincode submitted from the dropdown's "Enter Pincode" input.
  // Writes/broadcasts it the same way pincode entry elsewhere on the site
  // does (header, cart drawer, EDD) - the shared 'pincodeUpdated' listener
  // below does the actual geocode-to-city resolution and store filtering,
  // and closes this panel once it's done (see hidePincodePanel() call at
  // the end of that listener). Kept as a single resolution path instead
  // of duplicating fetchCityFromPincode() here to avoid two concurrent
  // geocode calls racing over the shared locationRequestId guard.
  function handlePincodeSearch() {
    if (!pincodeInput) return;
    const pincode = pincodeInput.value.trim();

    if (!pincode) {
      showPincodeInputError('Please enter a pincode');
      return;
    }
    if (!/^\d{6}$/.test(pincode) || pincode === '000000') {
      showPincodeInputError('Please enter a valid 6-digit pincode');
      return;
    }
    hidePincodeInputError();
    showLocationLoading('Finding stores near you…');

    sessionStorage.setItem('userPincode', pincode);
    const desktopPincodeElem = document.querySelector('#location__pincode');
    const mobilePincodeElem = document.querySelector('#mobile_pincode_value');
    const popupInput = document.querySelector('#popuppincode-input');
    if (desktopPincodeElem) desktopPincodeElem.innerHTML = pincode;
    if (mobilePincodeElem) mobilePincodeElem.innerHTML = pincode;
    if (popupInput) popupInput.value = pincode;
    window.dispatchEvent(new CustomEvent('pincodeUpdated', { detail: { pincode } }));
    if (typeof fetchDeliveryDate === 'function') {
      fetchDeliveryDate();
    }
  }

  // Handle "Use My Location" click
  function handleUseMyLocation() {
    const elements = getCustomDropdownElements();
    if (!elements.useLocationItem) return;

    // Show loading state
    const originalText = elements.useLocationItem.querySelector('span');
    if (originalText) {
      originalText.textContent = 'Getting location...';
    }

    // Close dropdown temporarily
    closeCustomDropdown();

    // "Use My Location" always wins over pincode-entry mode
    hidePincodePanel();

    // Check if geolocation is available
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser.');
      if (originalText) {
        originalText.textContent = 'Use My Location';
      }
      return;
    }

    // A real user click always wins over whatever page-load/pincode resolution
    // may still be in flight - bump the shared request id now, synchronously,
    // so a slower stale result can't overwrite this click's outcome later.
    const requestId = ++locationRequestId;
    showLocationLoading('Detecting your location…');

    // Get user's location
    // header.liquid blocks any getCurrentPosition call that isn't flagged as a real user click
    window.ZIGLY_MANUAL_LOCATION_CLICK = true;
    navigator.geolocation.getCurrentPosition(
      function(position) {
        const latitude = position.coords.latitude;
        const longitude = position.coords.longitude;
        // GPS coordinates are the most precise location signal available, so store them
        // directly rather than waiting on the (city-level) reverse-geocode lookup below.
        setUserCoords(latitude, longitude);

        getGeocoder()
          .then((geocoder) => withTimeout(geocoder.geocode({ location: { lat: latitude, lng: longitude } }), GEOCODE_TIMEOUT_MS))
          .then(function({ results }) {
            if (requestId !== locationRequestId) return; // superseded by a newer request
            let cityResolved = false;

            if (results && results.length) {
              const postcode = extractAddressComponent(results, ['postal_code']);
              if (postcode) {
                sessionStorage.setItem('userPincode', postcode);

                // Sync pincode display in header
                const desktopPincodeElem = document.querySelector('#location__pincode');
                const mobilePincodeElem = document.querySelector('#mobile_pincode_value');
                const popupInput = document.querySelector('#popuppincode-input');
                if (desktopPincodeElem) desktopPincodeElem.innerHTML = postcode;
                if (mobilePincodeElem) mobilePincodeElem.innerHTML = postcode;
                if (popupInput) popupInput.value = postcode;
              }

              const detectedCity = extractAddressComponent(results, ['locality', 'postal_town', 'administrative_area_level_2']);
              if (detectedCity) {
                const matchingCity = findMatchingCity(getMappedCity(detectedCity));
                if (matchingCity) {
                  selectCityFromCustomDropdown(matchingCity);
                  cityResolved = true;
                }
              }
            }

            if (!cityResolved) {
              // We already have GPS coordinates even though we couldn't resolve a
              // served city - notifyLocationOutcome shows the "near you" fallback
              // instead of the error, and results re-render ordered nearest-first.
              notifyLocationOutcome(false);
              filterAndRender();
            }

            // Reset text
            if (originalText) {
              originalText.textContent = 'Use My Location';
            }
          })
          .catch(function(error) {
            console.error('Error fetching location data:', error);
            if (requestId !== locationRequestId) return; // superseded by a newer request
            notifyLocationOutcome(false);
            filterAndRender();
            if (originalText) {
              originalText.textContent = 'Use My Location';
            }
          });
      },
      function(error) {
        console.error('Error getting location:', error.message);
        if (requestId !== locationRequestId) return; // superseded by a newer request
        notifyLocationOutcome(false);
        if (originalText) {
          originalText.textContent = 'Use My Location';
        }
      }
    );
  }

  // ============================================================================
  // AREA SUGGESTIONS FUNCTIONS
  // ============================================================================

  // Show suggestions based on area input
  let suggestionsDebounceTimer = null;
  let suggestionsRequestId = 0;
  let lastPlacePredictions = []; // indexed to match data-prediction-index on rendered items

  function showAreaSuggestions(searchTerm) {
    if (!areaSuggestionsMenu || !searchTerm || searchTerm.length < 2) {
      hideAreaSuggestions();
      return;
    }

    const searchLower = searchTerm.toLowerCase().trim();

    areaSuggestionsMenu.innerHTML = '<div class="store-locator__suggestion-no-results">Searching...</div>';
    areaSuggestionsMenu.style.display = 'block';

    // Places autocomplete is billed per session, so debounce it rather than
    // firing a request on every keystroke.
    clearTimeout(suggestionsDebounceTimer);
    suggestionsDebounceTimer = setTimeout(async () => {
      const requestId = ++suggestionsRequestId;
      const placePredictions = await fetchPlaceAutocompleteSuggestions(searchTerm);
      if (requestId !== suggestionsRequestId) return; // a newer keystroke superseded this
      if (!areaInput || areaInput.value.trim().toLowerCase() !== searchLower) return; // text changed since
      renderAreaSuggestions(placePredictions);
    }, 300);
  }

  // Renders the Places-suggestion dropdown and wires up click handling.
  function renderAreaSuggestions(placePredictions) {
    if (!areaSuggestionsMenu) return;
    lastPlacePredictions = placePredictions;

    if (placePredictions.length === 0) {
      areaSuggestionsMenu.innerHTML = '<div class="store-locator__suggestion-no-results">No suggestions found</div>';
      return;
    }

    areaSuggestionsMenu.innerHTML = placePredictions.map((prediction, index) => {
      const mainText = prediction.mainText ? prediction.mainText.toString() : prediction.text.toString();
      const secondaryText = prediction.secondaryText ? prediction.secondaryText.toString() : '';
      return `
        <div class="store-locator__suggestion-item" data-prediction-index="${index}" data-suggestion-value="${mainText}">
          <div class="store-locator__suggestion-name">${mainText}</div>
          <div class="store-locator__suggestion-address">${secondaryText}</div>
        </div>
      `;
    }).join('');

    const items = areaSuggestionsMenu.querySelectorAll('.store-locator__suggestion-item');
    items.forEach((item) => {
      item.addEventListener('click', function(e) {
        e.stopPropagation();
        const value = this.getAttribute('data-suggestion-value');
        if (!areaInput) return;

        areaInput.value = value;
        hideAreaSuggestions();
        // Clear city selection when suggestion is picked
        if (cityInput) {
          cityInput.value = '';
          syncCustomDropdownWithSelect();
        }

        const index = parseInt(this.getAttribute('data-prediction-index'), 10);
        const prediction = lastPlacePredictions[index];
        if (prediction) {
          resolvePlaceSuggestion(prediction, value);
        }
      });
    });
  }

  // Hide area suggestions
  function hideAreaSuggestions() {
    if (areaSuggestionsMenu) {
      areaSuggestionsMenu.style.display = 'none';
    }
  }

  // Setup event listeners
  function setupEventListeners() {
    // Custom dropdown event listeners
    const elements = getCustomDropdownElements();

    // Trigger button click
    if (elements.trigger) {
      elements.trigger.addEventListener('click', function(e) {
        e.stopPropagation();
        toggleCustomDropdown();
      });
    }

    // Close button click
    if (elements.closeBtn) {
      elements.closeBtn.addEventListener('click', function(e) {
        e.stopPropagation();
        closeCustomDropdown();
      });
    }

    // Dropdown search input handler
    if (elements.searchInput) {
      elements.searchInput.addEventListener('input', function(e) {
        e.stopPropagation();
        const searchTerm = this.value;
        filterCitiesInDropdown(searchTerm);
      });

      // Prevent dropdown from closing when clicking on search input
      elements.searchInput.addEventListener('click', function(e) {
        e.stopPropagation();
      });
    }

    // Use My Location click
    if (elements.useLocationItem) {
      elements.useLocationItem.addEventListener('click', function(e) {
        e.stopPropagation();
        handleUseMyLocation();
      });

      // Keyboard support
      elements.useLocationItem.addEventListener('keypress', function(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          handleUseMyLocation();
        }
      });
    }

    // Enter Pincode click
    if (elements.enterPincodeItem) {
      elements.enterPincodeItem.addEventListener('click', function(e) {
        e.stopPropagation();
        showPincodePanel();
      });

      // Keyboard support
      elements.enterPincodeItem.addEventListener('keypress', function(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          showPincodePanel();
        }
      });
    }

    // Pincode search input handlers
    if (pincodeInput) {
      pincodeInput.addEventListener('input', function() {
        this.value = this.value.replace(/\D/g, '');
        hidePincodeInputError();
      });

      pincodeInput.addEventListener('keypress', function(e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          handlePincodeSearch();
        }
      });

      pincodeInput.addEventListener('click', function(e) {
        e.stopPropagation();
      });
    }

    if (pincodeSubmitBtn) {
      pincodeSubmitBtn.addEventListener('click', function(e) {
        e.stopPropagation();
        handlePincodeSearch();
      });
    }

    // City items click
    if (elements.cityItems && elements.cityItems.length > 0) {
      elements.cityItems.forEach(item => {
        item.addEventListener('click', function(e) {
          e.stopPropagation();
          const cityValue = this.getAttribute('data-city-value');
          if (cityValue) {
            selectCityFromCustomDropdown(cityValue);
          }
        });

        // Keyboard support
        item.addEventListener('keypress', function(e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            const cityValue = this.getAttribute('data-city-value');
            if (cityValue) {
              selectCityFromCustomDropdown(cityValue);
            }
          }
        });
      });
    }

    // Close dropdowns when clicking outside
    document.addEventListener('click', function(e) {
      const clickedInsidePincodePanel = pincodePanel && pincodePanel.contains(e.target);
      if (elements.wrapper && !elements.wrapper.contains(e.target)) {
        closeCustomDropdown();
        if (!clickedInsidePincodePanel) {
          exitPincodeMode();
        }
      }
      if (areaSuggestionsMenu && !areaInput.contains(e.target) && !areaSuggestionsMenu.contains(e.target)) {
        hideAreaSuggestions();
      }
    });

    // Close dropdowns on Escape key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') {
        closeCustomDropdown();
        hideAreaSuggestions();
        exitPincodeMode();
      }
    });

    // City dropdown handler (for hidden select - compatibility)
    if (cityInput) {
      cityInput.addEventListener('change', function() {
        lastSelectedCity = this.value.trim() || null;
        updateAreaSearchVisibility();
        // Sync custom dropdown
        syncCustomDropdownWithSelect();
        // Hide error/info messages when user manually selects a city
        hideErrorMessage();
        hideInfoMessage();
        // Clear area search when city search is active
        if (this.value.trim() !== '' && areaInput) {
          areaInput.value = '';
          // Hide area clear button
          const areaClearBtn = document.getElementById('store-locator-area-clear-btn');
          const areaInputWrapper = areaInput.closest('.store-locator__search-input-wrapper');
          if (areaClearBtn) {
            areaClearBtn.style.display = 'none';
          }
          if (areaInputWrapper) {
            areaInputWrapper.classList.remove('store-locator__area-input-has-value');
          }
        }
        filterAndRender();
      });
    }

    // Area input handler
    if (areaInput) {
      const areaInputWrapper = areaInput.closest('.store-locator__search-input-wrapper');
      
      // Toggle clear button visibility
      const toggleAreaClearButton = () => {
        const clearBtn = document.getElementById('store-locator-area-clear-btn');
        if (clearBtn && areaInputWrapper) {
          if (areaInput.value.trim() !== '') {
            clearBtn.style.display = 'flex';
            areaInputWrapper.classList.add('store-locator__area-input-has-value');
          } else {
            clearBtn.style.display = 'none';
            areaInputWrapper.classList.remove('store-locator__area-input-has-value');
          }
        }
      };

      // Show/hide clear button on input
      areaInput.addEventListener('input', function() {
        toggleAreaClearButton();

        // Show suggestions
        showAreaSuggestions(this.value);

        // Clear city search when area search is active
        if (this.value.trim() !== '' && cityInput) {
          cityInput.value = '';
          // Sync custom dropdown
          syncCustomDropdownWithSelect();
        }
        // Editing the text invalidates any previous submitted search for it
        areaSearchSubmitted = false;
        filterAndRender();
      });

      // Clear button click handler
      const areaClearBtn = document.getElementById('store-locator-area-clear-btn');
      if (areaClearBtn) {
        areaClearBtn.addEventListener('click', function(e) {
          e.stopPropagation();
          if (areaInput) {
            areaInput.value = '';
            areaSearchSubmitted = false;
            autocompleteSessionToken = null;
            toggleAreaClearButton();
            hideAreaSuggestions();
            filterAndRender();
          }
        });
      }

      // Initial check for clear button visibility
      toggleAreaClearButton();
    }

    // Search button handler
    if (searchButton) {
      searchButton.addEventListener('click', function() {
        clearTimeout(debounceTimer);
        performAreaSearch();
      });
    }

    // Reset button handler
    if (resetButton) {
      resetButton.addEventListener('click', function() {
        resetSearch();
      });
    }

    // "Enable location" banner button - reuses the exact same geolocation +
    // reverse-geocode flow as the dropdown's "Use My Location" item.
    if (locationBannerBtn) {
      locationBannerBtn.addEventListener('click', function() {
        handleUseMyLocation();
      });
    }

    // Enter key handlers (only for area input, city is now dropdown)
    if (areaInput) {
      areaInput.addEventListener('keypress', function(e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (searchButton) searchButton.click();
        }
      });
    }

    // Listen for pincode updates from header
    window.addEventListener('pincodeUpdated', async function(event) {
      const pincode = event.detail.pincode;
      if (pincode && cityInput) {
        shouldScrollToError = true;
        const requestId = ++locationRequestId;
        const apiCity = await fetchCityFromPincode(pincode);
        if (requestId !== locationRequestId) return; // superseded by a newer request
        if (apiCity) {
          // apiCity is already mapped from fetchCityFromPincode, so use it directly
          const matchingCity = findMatchingCity(apiCity);

          if (matchingCity) {
            cityInput.value = matchingCity;
            notifyLocationOutcome(true, matchingCity);
            // Trigger change event to filter stores
            cityInput.dispatchEvent(new Event('change', { bubbles: true }));
            shouldScrollToError = false;
          } else {
            // No dropdown city matched - keep the filter blank (shows all stores,
            // sorted nearest-first if we have coordinates)
            cityInput.value = '';
            // Clear area search
            if (areaInput) {
              areaInput.value = '';
            }
            notifyLocationOutcome(false);
            filterAndRender();
          }
        } else {
          notifyLocationOutcome(false); // Geocoding failed entirely - fall back to distance sort if we have coords
          shouldScrollToError = false;
          // The pincode is still known even though it couldn't be resolved to
          // a city - re-render so results are ordered nearest-first.
          filterAndRender();
        }
        // Harmless no-op unless the dropdown's own "Enter Pincode" input
        // triggered this resolution, in which case it's done with it now.
        // notifyLocationOutcome() above already set the trigger label
        // correctly, so just hide the panel rather than resetting it.
        hidePincodePanel();
      }
    });

    // Handle browser back/forward buttons
    window.addEventListener('popstate', function(event) {
      const urlPage = getURLParameter('page');
      if (urlPage) {
        const pageNum = parseInt(urlPage, 10);
        if (!isNaN(pageNum) && pageNum > 0) {
          const totalPages = Math.ceil(filteredStores.length / STORES_PER_PAGE);
          if (pageNum <= totalPages) {
            currentPage = pageNum;
            renderStores();
            // Scroll to section with offset
            const section = document.getElementById('store-locator-section');
            if (section) {
              const sectionTop = section.getBoundingClientRect().top + window.pageYOffset;
              const scrollPosition = sectionTop - 150;
              window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
            }
          }
        }
      } else {
        // No page parameter, go to page 1
        currentPage = 1;
        renderStores();
      }
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();